// @ts-strict-ignore
/**
 * Properties menu: clickable list of B2B, User or Event properties and cohorts to go inside mp-drop-menu
 */
import React, {createRef} from 'react';
import compact from 'lodash/compact';
import get from 'lodash/get';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import orderBy from 'lodash/orderBy';
import omit from 'lodash/omit';
import partition from 'lodash/partition';
import pick from 'lodash/pick';
import uniq from 'lodash/uniq';
import keyBy from 'lodash/keyBy';
import isNil from 'lodash/isNil';
import pickBy from 'lodash/pickBy';
import stableStringify from 'json-stable-stringify';
import memoize from 'lodash/memoize';
import clsx from 'clsx';

import {debounce} from 'common/util/lodash-wrapped';
import {getNow} from 'common/util/timing';
import {
  DISABLED_DUE_TO_CLASSIFIED_DATA,
  MappedPropertyValues,
  Permission,
  ProjectFeatureFlag,
  PropertyType,
  ReportName,
  ResourceType,
  SCD_COLUMN_SELECTION_LIMIT,
  SCD_PROPERTY_DISABLED_REASON,
  SPECIAL_EVENTS,
  STRING_TYPECAST_PROPERTIES,
  TYPECAST_PROPERTY_TYPES,
} from 'common/report/constants';
import {
  eventDefinitionParamsAndKey,
  fetchAllPropertyDefinitionsForGroupProfiles,
  fetchGlobalEventPropertyDefinitions,
  fetchPropertyDefinitionsForEvents,
  fetchPropertyDefinitionsForUserProfiles,
  getAllGlobalPropertyDefinitions,
  getAllPropertyDefinitionsForEvents,
  getAllPropertyDefinitionsForUserProfiles,
  groupPropertyDefinitionsStore,
  isQueryOnAllEvents,
  peoplePropertyDefinitionsStore,
  propertyDefinitionsForEventsStore,
} from 'common/report/stores/property-definitions';
import {userDetailsStore} from 'common/report/stores/user-details';
import {assert, dataGroupIDsEqual, replaceByIndex} from 'common/util';
import {Cohort as CohortModel} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {
  isSerializedInlineCohort,
  SerializedCohort,
  SerializedInlineCohort,
  User,
} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {promptInlineCohorts} from 'common/widgets/inline-cohorts-modal/util';
import {INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING} from 'common/widgets/property-filter-menu/models/insights-filter';
import {EventPropertiesClient} from 'common/report/queries/query-api/event-properties-client';
import {PeoplePropertiesClient} from 'common/report/queries/query-api/people-properties-client';
import {cohortsStore} from 'common/report/stores/cohorts';
import {customPropertiesStore, fetchCustomProperties} from 'common/report/stores/custom-properties';
import {
  getIconForPropertyType,
  getLabelForPropertyType,
  isInvalidScdPropertyType,
  isScdPropertyName,
  renamePropertyValue,
  renameSCDProp,
} from 'common/report/util/properties';
import {Cohort, PropertyDefinition} from 'common/util/definitions-types';
import {
  getInlineCustomPropertyId,
  isBehavioralCustomProperty,
  isBorrowedPropertyReferenced,
  isInlineCustomProperty,
  isPersistedCustomProperty,
} from 'common/widgets/custom-property-modal/util';
import {promptSharing} from 'common/widgets/sharing';
import {ActionType, ContextPanelData} from 'common/widgets/context-panel/types';
import {getTooltipDataForCohort, getTooltipDataForProperty} from 'common/widgets/context-panel/util';
import {SavedCohortItem} from 'common/query-builder/query-entry-section/types';
import {CustomProperty, SharableEntityType} from 'common/report/util/saved-entities/types';
import {isAllowedSuperadminOverride} from 'common/report/util/saved-entities';
import {
  eventPrefix as shareEventPrefix,
  ShareButtonUISource,
  track as shareTrack,
} from 'common/widgets/sharing/tracking';
import {
  getTempLookupTableMappings,
  LookupTableProperty,
  lookupTablesStore,
  registerTempLookupTableMapping,
} from 'common/report/stores/lookup-tables';
import {
  ExpanderItemClickEvent,
  ExpansionToggleLabelMode,
  ItemsMenuSize,
  SearchMatchResult,
  Section,
  SectionItem,
  SelectAllMechanism,
  SelectedTheme,
  SubLabelTheme,
  TooltipInfo,
} from 'common/components/items-menu/types';
import {reporter} from 'common/report/error-reporting';
import {assertUnreachable} from 'common/util/typescript';
import {MPProperties, PATH_SEPARATOR} from 'common/constants';
import {queryBuilderUtils} from 'common/query-builder/query-builder/util';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {captureWorkflowTiming} from 'common/util/workflow-timing';
import {useStore} from 'common/util/component-helpers';
import {fuzzyMatch} from 'common/util/fuzzy-match';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {getPropertyPathPrefix} from 'common/query-builder/query-block/utils/property-util';
import {
  getMenuItemForBehavior,
  getPrefixForBehavior,
  hasDynamicLabel,
  isMultiAttributionBehavior,
} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {getProjectId, getReportName, getWorkspaceId} from 'common/report/util';
import {Behavior} from 'common/types/reports/query';
import {formatDataGroupLabel} from 'common/query-builder/query-entry-section/util';
import {promisifyModal} from 'common/components/modal/utils';
import {Visibility} from 'common/components/base/stackable';
import type {CustomPropertyBuilderAttrs} from 'common/widgets/custom-property-modal';
import {CurrentProject} from 'common/report/queries/app-api/project-metadata-client';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {emitTrackEvent, propagateEvent} from 'common/util/dom';
import {TrackingUsageContext} from 'common/report/tracking-setup';
import {withStyles} from 'common/util/react';
import {spans} from 'common/util/tracing';
import {ItemsMenuReact} from 'common/components/items-menu';
import {ResizablePanelReact} from 'common/components/resizable-panel';
import {Direction, PanelName} from 'common/components/resizable-panel/types';
import {ButtonReact} from 'common/components/button';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {SkeletonListReact} from 'common/components/skeleton-list';
import {SkeletonListRowSize} from 'common/components/skeleton-list/types';
import {IronContextRegistry} from 'common/report/context-registry';
import {useEventsController} from 'common/contexts/react/events-controller-context';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {useTranslation} from 'common/i18n/hooks';
import {eventPropertyValuesClient} from 'common/report/queries/query-api/event-property-values-client';
import {
  AutoDetectExperimentKeys,
  getExperimentKey,
} from 'apps/experiment-reporting/src/experiment-reporting-app/constants';
import {InlineCohortsModalProps} from 'common/widgets/inline-cohorts-modal/types';
import 'common/widgets/custom-property-modal';
import {TooltipReact} from 'common/components/tooltip';
import {Translatable} from 'common/i18n';

import {EventsControllerSectionConfig} from '../contexts/events-controller';
import {EventItem} from '../events-menu/types';
import {filterToBookmarkableEventItem} from '../events-menu/util';
import {InlineCohortSegment} from '../segment-menu/types';
import {ChildMenuInterface, ChildMenuItemsCache, TooltipContextActionResult} from '../child-menu';
import {NUM_RECENT_ITEMS, TRACKING_EVENTS} from '../constants';
import {getChildMenuHeight, getOpenedMenuComponent, OpenedMenuComponent, sortItemsByLabel} from '../utils/util';
import {getSingleDataGroupId} from '../utils/data-group-util';
import {MenuSearchBar} from '../mixins/search-bar';
import {
  calculatePropertiesIntersection,
  commitCohort,
  customPropHasGroupProperty,
  fetchAllLookupTableProperties,
  filterToBookmarkablePropertyItem,
  getCohortPropertyItem,
  getDataDefinitionsPropertyMenuItem,
  getDataGroupIdForCustomProp,
  getItemUniqueKey,
  getPropertyDefinitions,
  getSelectedLabel,
  getSelectedLeftIcon,
  getSerializedCohorts,
  getTooltipNameForPropertyItem,
  getTooltipPathForPropertyItem,
  inferStagedItemType,
  propertyItemToObjectPropertyItem,
  sanitizeCustomProperty,
  storeCustomPropertyToProperty,
  transformCohortToCohortItem,
} from './util';
import {TrackingEvent} from './tracking';
import {
  AllTab,
  ApplyButtonDisabledReason,
  CohortItem,
  DataGroupItem,
  EventsInPropertiesMenuTab,
  FilterSectionItem,
  FilterValue,
  IncludeCustomPropertyType,
  InlineCohort,
  isCohort,
  isEventSectionItem,
  isInlineCohort,
  isProperty,
  isPropertySectionItem,
  isSavedCohort,
  Item,
  MenuItemType,
  PropertiesMenuParams,
  Property,
  PropertyCombineMode,
  PropertyItemAction,
  PropertyItemPersistedIdentifierKeys,
  SavedCohort,
  Selection,
  SortBy,
  SpecialActions,
  SpecialControl,
  StagedItemType,
  TypecastPillAction,
} from './types';
import {
  CohortExtras,
  CombinedSection,
  EventOrPropertySectionItem,
  EventSection,
  EventSectionItem,
  PropertiesItemClickEvent,
  PropertyItemState,
  PropertySection,
  PropertySectionItem,
  SectionItemProperty,
} from './item-menu-types';
import {
  AllFilter,
  BORROW_PROPERTY,
  DATASET_PROPERTY,
  DATETIME_UNIT_OPTION_SECTION_ITEMS,
  EXCLUDED_PROPERTY_TYPES_FOR_BORROW_PROP,
  FIRST_EVENT_TIME_PROPERTY,
  LIST_LENGTH,
  LIST_PROPERTY_SUM,
  LIST_PROPERTY_UNIQUES,
  OTHER_OPTIONS,
  SPECIAL_CONTROLS,
  SPECIAL_PROPERTIES,
} from './constants';
import css from './index.inline.styl';

// number of items to show per section on the "All" tab
// remaining items are hidden under a `Show all` expander
const MENU_ITEM_LIMIT = 10;
const RECENT_ITEM_TIMESTAMP_KEY = `lastUsedTimestamp` as const;

interface PropertiesMenuState {
  selected: any;
  dataGroups: {[id: string]: DataGroupItem};
  permissionList: any;
  loadingCohorts: boolean;
  loadingExperiments: boolean;
  loadingEventProperties: boolean;
  loadingGlobalEventProperties: boolean;
  loadingPeopleProperties: boolean;
  stagedItemsStack: EventOrPropertySectionItem[];
  /** The current screen of the properties-menu. This will be null if the current screen is the top-level menu */
  stagedItemType: StagedItemType | null;
  searchFilter: string;
  isLoading: boolean;
  isLoadingDataGroups: boolean;
  selectedFilter: FilterValue;
}

async function promptCustomProperties(
  attrs,
): Promise<{appliedValue: any; didClose: boolean; deletedId: string | number; hasGroupProperty: boolean}> {
  const closeEv = await promisifyModal({
    tagName: `mp-custom-property-modal`,
    attrs,
    closeEvents: [`apply`, `cancel`, `change`, `delete`],
  });
  const customProp = closeEv.detail?.property?.customProperty;

  return {
    appliedValue: closeEv.type === `apply` ? closeEv.detail?.property : null,
    didClose: closeEv.type === `cancel` || (closeEv.type === `change` && closeEv.detail.state === Visibility.Closed),
    deletedId: closeEv.type === `delete` ? closeEv.detail.customPropertyId : null,
    hasGroupProperty: customProp ? customPropHasGroupProperty(customProp) : false,
  };
}

export class PropertiesMenu
  extends React.PureComponent<
    PropertiesMenuParams & Pick<IronContextRegistry, `eventsController` | `currentProject` | `i18n`>,
    PropertiesMenuState
  >
  implements ChildMenuInterface
{
  private rootEl = createRef<HTMLDivElement>();

  private searchInputEl = createRef<HTMLInputElement>();

  private experimentNames: string[] = [];
  private propertiesMenuSpanId = `properties-menu`;
  private propertiesMenuLoadingSpanId = `properties-menu.loading`;

  // Handle unsaved -> saved conversion on store change
  private cohortStoreCallback = () => {
    this.setState({loadingCohorts: false});
    this.getTooltipData.cache.clear();

    const savedCohortItems = this.getSavedCohortSelection();
    savedCohortItems.forEach((item, index) => {
      const canonicalCohort = cohortsStore.value[item.value] as SerializedCohort;
      if (item.label && canonicalCohort && canonicalCohort.name !== item.label) {
        this.replaceItem(
          {
            resourceType: ResourceType.Cohort,
            label: canonicalCohort.name,
            value: item.value,
          },
          index,
          {autoTriggered: true},
        );
      }
    });

    const inlineCohorts = this.getInlineCohortSelection();
    inlineCohorts.forEach(({value}, index) => {
      if (!value.cohort.raw_cohort.unsavedId) {
        return;
      }

      const unsavedCohort = cohortsStore.value[value.cohort.raw_cohort.unsavedId] as SerializedInlineCohort;

      if (!unsavedCohort) {
        return;
      }

      // Inline cohort converted
      if (Number.isInteger(unsavedCohort.id)) {
        const cohort = cohortsStore.value[unsavedCohort.id];

        this.replaceItem(getCohortPropertyItem(cohort.name, cohort.created_by, cohort.id), index, {
          autoTriggered: true,
        });
      } else if (!isEqual(unsavedCohort.raw_cohort, value.cohort.raw_cohort)) {
        // Inline cohort updated
        this.replaceItem(
          getCohortPropertyItem(unsavedCohort.name, unsavedCohort.created_by, {cohort: unsavedCohort}),
          index,
          {autoTriggered: true},
        );
      }
    });
  };

  private lookupTablesStoreCallback = ({fromUpdate}) => {
    if (fromUpdate) {
      // callback triggered from manual #update call (e.g. mapping registered or unregistered)
      return;
    }
    this.registerTempLookupTableMappingFromSelected();
    fetchAllLookupTableProperties(this.props.i18n);
    this.getTooltipData.cache.clear();
  };

  private trackListener = (ev: CustomEvent) => {
    this.props.onTrack?.(ev);
  };

  private _cache = ChildMenuItemsCache.createCache(
    this.props.userId || userCookieUtils.getUserIdentityFromCookie()?.id,
    this.sourceAttrs.projectId,
    `properties-menu`,
    this.sourceAttrs.workspaceId,
    15,
  );

  private _cachedEventProperties: PropertyDefinition[] = null;
  private _recentlyUsedUniqueKeys: Set<string> = null;

  /** perf tracking properties */
  private declare _openedAt: number;
  private _openCount = 0;
  private _instanceEventLoadHistory = new Set();
  // We compute this right after calculating menu sections. We store it in a property rather than relying on computing
  // it at tracking time because tracking happens async at which point cached sections could've been invalidated
  // already
  private declare _numTotalItemsOnPrevRender: number;

  private declare _eventPropertiesClient: EventPropertiesClient;
  private declare _peoplePropertiesClient: PeoplePropertiesClient;

  // this is to support the workflow timers
  private _onDisconnected = [];
  public onDisconnected(fn: () => void) {
    this._onDisconnected.push(fn);
  }

  static defaultProps: PropertiesMenuParams = {
    minSelection: 0, // overridden by null from query-block
    disabledItems: {},
    includeCustomProperties: IncludeCustomPropertyType.None,
    propertyCombineMode: PropertyCombineMode.Union, // overridden by null from query-block
    specialCohorts: [], // overridden by null from query-block
    otherOptions: [],
    selectAllMechanism: SelectAllMechanism.Full,
    menuItemTypes: [], // overridden by null from query-block
    typeFilter: [],
  };

  state: PropertiesMenuState = {
    dataGroups: {},
    permissionList: [],
    isLoading: false,
    loadingCohorts: false,
    loadingExperiments: false,
    loadingEventProperties: false,
    loadingGlobalEventProperties: false,
    loadingPeopleProperties: false,
    selected: this.props.selected,
    // If stagedItemType is null, it means it's at the top level
    stagedItemType: null,
    searchFilter: ``,
    stagedItemsStack: [],
    isLoadingDataGroups: false,
    selectedFilter: AllFilter.value,
  };

  constructor(props) {
    super(props);
    this._eventPropertiesClient = new EventPropertiesClient(this.props.i18n);
    this._peoplePropertiesClient = new PeoplePropertiesClient(this.props.i18n);
  }

  componentDidMount() {
    this.setOpenedAt(this.props.open);

    this.updateFromSelection();

    // This is in case if we haven't already pre-fetched the dataGroupStore and group properties
    // TODO: (Akshay) - We should eventually convert all of these dataGroupStore calls into a separate model resource
    // and incorporate into the App's context
    if (this.props.allDataGroups) {
      this.setState({isLoadingDataGroups: true});
      useStore(this, dataGroupStore, () => this.loadDataGroups()).fetch({
        projectId: this.props.projectId,
      });
    }

    this.fetchData();

    fetchCustomProperties({callback: this.customPropertiesStoreCallback});

    this.fetchCohorts();
    this.fetchLookupTables();
    this.fetchExperiments();

    this.loadStoreValues();
    this.maybeNavigateToSelected();

    this.rootEl.current?.addEventListener(`track`, this.trackListener);

    spans.getOrCreateSpan({id: this.propertiesMenuSpanId});
    spans.getOrCreateSpan({id: this.propertiesMenuLoadingSpanId, parentId: this.propertiesMenuSpanId});
  }

  componentDidUpdate(prevProps: PropertiesMenuParams, prevState: PropertiesMenuState) {
    if (prevProps.open !== this.props.open) {
      // do this before any other code executes
      this.setOpenedAt(prevProps.open);
    }

    this.props.onRender?.();

    const changedParams = [...Object.keys(this.props).filter((key) => !isEqual(this.props[key], prevProps[key]))];

    if (changedParams.includes(`selected`)) {
      // Ensure `this.updateFromSelection()` is called before the cohort store is updated (done in `loadStoreValues()`)
      // because fetching cohorts can trigger item replacements by calling `replaceItem()`
      // which can misbehave if this.updateFromSelection() hasn't updated `this.state.selectedFilter` from its default value.
      //
      // It misbehaves because `replaceItem()` does different things depending on whether `this.helpers.useMultipleSelection()` is true,
      // and `this.helpers.useMultipleSelection()` depends on `this.state.selectedFilter`.
      this.setState(
        {
          selected: this.props.selected,
        },
        () => {
          this.updateFromSelection();
          this.loadStoreValues();
          this.customPropertiesStoreCallback();
          if (this.props.open) {
            this.maybeNavigateToSelected();
          }
        },
      );
    }

    if (
      [
        `allDataGroups`,
        `dataGroupId`,
        `dataGroupIds`,
        `eventNames`,
        `includeCustomProperties`,
        `projectHasCohorts`,
        `hideEvents`,
        `projectHasPeople`,
        `projectId`,
        `propertyCombineMode`,
      ].some((key) => changedParams.includes(key))
    ) {
      // Ensure `this.updateFromSelection()` is called before `fetchCohorts()` (which is also called in `fetchData()`)
      // because fetching cohorts can trigger item replacements by calling `replaceItem()`
      // which can misbehave if this.updateFromSelection() hasn't updated `this.state.selectedFilter` from its default value.
      //
      // It misbehaves because `replaceItem()` does different things depending on whether `this.helpers.useMultipleSelection()` is true,
      // and `this.helpers.useMultipleSelection()` depends on `this.state.selectedFilter`.
      this.updateFromSelection();
      this.fetchData();
      this.fetchCohorts();
      this.fetchLookupTables();
      this.customPropertiesStoreCallback();
    } else if (changedParams.includes(`open`)) {
      this.resetStage();
      if (this.props.open) {
        this.trackEvent(TrackingEvent.OpenPropertiesDropDown);
        this.maybeNavigateToSelected();
      } else {
        this.setState({
          searchFilter: ``,
        });
      }
    }

    if (prevState.isLoading !== this.state.isLoading && this.state.isLoading === false) {
      spans.endSpanById(this.propertiesMenuLoadingSpanId);
    }
  }

  componentWillUnmount() {
    customPropertiesStore.unsubscribe(this.customPropertiesStoreCallback);
    propertyDefinitionsForEventsStore.unsubscribeAll(this.eventPropertiesDefinitionsStoreCallback);
    peoplePropertyDefinitionsStore.unsubscribeAll(this.userPropertiesDefinitionsStoreCallback);

    lookupTablesStore.unsubscribe(this.lookupTablesStoreCallback);

    if (this.props.projectHasCohorts) {
      const dataGroupId = getSingleDataGroupId(this.props.dataGroupIds);
      if (dataGroupId && this.props.supportB2bCohorts) {
        cohortsStore.unsubscribe(this.cohortStoreCallback, dataGroupId);
      } else {
        cohortsStore.unsubscribe(this.cohortStoreCallback);
      }
    }

    this.rootEl.current?.removeEventListener(`track`, this.trackListener);

    this._onDisconnected.forEach((fn) => fn());

    spans.endSpanById(this.propertiesMenuSpanId);
  }

  get t() {
    return this.props.i18n.t;
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  UNSAFE_componentWillUpdate(_, nextState: Readonly<PropertiesMenuState>): void {
    // this is to maintain existing caching behavior on this component. we need to clear cache before next render so new data can be used at render time
    // this caching logic should be replaced by useMemo in a functional component

    // Setting searchInputEl in state causes a re-render when opening existing property menu. We can reuse menu
    // sections in this case rather than recomputing it.
    const nonCacheInvalidatingKeys = new Set([`searchInputEl`]);
    const useCachedSections = Object.keys(nextState).every((key) => nonCacheInvalidatingKeys.has(key));
    if (!useCachedSections) {
      this._cachedEventProperties = null;
      this._recentlyUsedUniqueKeys = null;
    }
  }

  private contextPanelTransform = (focusedItem: PropertySectionItem | EventSectionItem): ContextPanelData | null => {
    const {print} = this.props.i18n;

    if (Object.values(SpecialActions).includes(focusedItem.value)) {
      return null;
    }

    // Since event and properties menu item have different data structures
    // if an item is an event, section does not have a type. Instead, focusedItem.menuItemTyp has the type we are looking for
    const focusedItemType = focusedItem.menuItemType;
    let tooltipData: ContextPanelData = null;
    if ([MenuItemType.Events, MenuItemType.EventWithProperties].includes(focusedItemType)) {
      // TODO: need to figure out how to get recently used events from the events-menu
      tooltipData = this.props.eventsController.getContextPanelData(focusedItem.value, {
        allowEdit: !this.props.readonly && !this.props.hideEditControls,
        throughPropertiesMenu: true,
      });
    } else {
      const propertyItem = focusedItem as PropertySectionItem;
      tooltipData = propertyItem.tooltipData || this.getTooltipData(propertyItem, propertyItem.resourceType);
      if (tooltipData && !tooltipData.entityIcon) {
        tooltipData.entityIcon = propertyItem.icon;
      }
    }

    // default tooltipData for entries that do not have definitions
    if (tooltipData === null && !focusedItem.itemFromInput) {
      tooltipData = {
        header: focusedItem?.label,
        subHeader: null,
        path: focusedItem.subLabel ? [print(focusedItem.subLabel).split(PATH_SEPARATOR)[0]] : null,
        entityIcon: focusedItem?.icon,
        description: ``,
        editItem: null,
        metrics: [],
        details: [],
        links: [],
        actions: [],
        isRecentlyUsed: focusedItem.isRecentlyUsed,
      };
    }

    return tooltipData;
  };

  private handleClickItem = async ({detail: {item, section, ...detail}}: PropertiesItemClickEvent) => {
    // Special Action - An item presents a Create Modal
    const isSpecialAction = Object.values(SpecialActions).includes(item.value);
    if (isSpecialAction) {
      if (item.value === SpecialActions.Refresh) {
        this.fetchData({refresh: true});
        this.fetchCohorts();
        this.fetchLookupTables();
        this.trackEvent(TRACKING_EVENTS.REFRESHED_PROPERTIES);
        return;
      } else if (item.value === SpecialActions.NavigateBack) {
        this.updateStagedItem(null, null);
        return;
      }
      // Todo: Type this as a new section
      this.showCreateItemModal(item.value);
      return;
    }

    // for the beta menu, we set the `menuItemType` on the item itself when we merge all of the sections to a single section
    // for the classic menu, we still have access to the original section so we get the type from there.
    const menuItemType = item.menuItemType || section.type;
    let itemValue = item;

    this.trackBorrowPropertySelection({menuItemType});

    // Stageable Item - An item keeps the user in the menu showing a new screen
    if (section.type !== MenuItemType.Events) {
      const propertyItem = item as PropertySectionItem;
      const stagedItemType = inferStagedItemType(propertyItem, this.useMultipleSelection());

      if (stagedItemType && this.canStageItemType(stagedItemType)) {
        if (SPECIAL_PROPERTIES.has(propertyItem.value)) {
          this.addSelectedItemToRecent(propertyItem);
        }

        this.updateStagedItem(item as PropertySectionItem, stagedItemType);
        return;
      } else if (stagedItemType === StagedItemType.Datetime) {
        // if the datetime stage is being prevented, auto-select day
        itemValue = {...(item as PropertySectionItem), unit: TimeUnit.Day};
      }
    }

    // List item that needs a subtype
    const isListProperty =
      (item as SectionItemProperty).type === PropertyType.List &&
      [
        MenuItemType.EventProperties,
        MenuItemType.UserProperties,
        MenuItemType.UserCustomProperties,
        MenuItemType.DataGroupProperties,
      ].includes(item.menuItemType);

    // lookup table property does not support `toptype` query so exclude those
    if (isListProperty && !(item as SectionItemProperty).isLookupTableProperty) {
      const property = item as SectionItemProperty;

      this.setState({isLoading: true});

      try {
        // look up list property subtype
        let propertyValue = property.value;
        let propertyName = property.name;
        let propertyObjectKey: string[];
        const client =
          property.resourceType === ResourceType.User ? this._peoplePropertiesClient : this._eventPropertiesClient;
        const stagedItemType = this.getStagedItemType();
        if (stagedItemType === StagedItemType.Object) {
          // if it's a list within an object, should apply propertyObjectKey
          const [baseProperty, ...nestedProps] = this.filterPropertyStagedItems();
          propertyObjectKey = [...nestedProps.map((nestedProperty) => nestedProperty.value), propertyValue];
          propertyValue = baseProperty.value;
          propertyName = baseProperty.name;
        }

        const queryParams = {
          projectId: getProjectId(),
          property: {
            resourceType: property.resourceType,
            propertyType: PropertyType.List,
            propertyName: propertyValue,
            propertyDefaultType: PropertyType.List,
            ...(propertyObjectKey ? {propertyObjectKey} : {}),
          },
          ...((item as SectionItemProperty).dataGroupId
            ? {dataGroupId: (item as SectionItemProperty).dataGroupId}
            : {}),
        };

        const properties =
          client.getCachedResponse(queryParams) || (await client.query(queryParams, {forceCache: true}));

        const rawSubType = properties.find((p) => p.name === propertyValue)?.type;

        // make sure the type we get back is valid and default to string if we can't determine it
        const subType = Object.values(PropertyType).includes(rawSubType)
          ? (rawSubType as PropertyType)
          : PropertyType.String;

        property.joinPropertyType = PropertyType.List;

        // Usually `property.type` should be the same as `subType`, however it is
        // used to determine the property type shown in the segmentation menu, so by
        // setting it to PropertyType.List here we ensure that list properties (particularly
        // nested list properties) are shown as list types in the segmentation menu.
        //
        // An exception here is a list of objects, which needs its type to be "object" to
        // surface the object property selection menu.
        property.type = subType === PropertyType.Object ? subType : PropertyType.List;
        property.propertyType = subType;
        property.propertyDefaultType = subType;
        if (subType === PropertyType.Datetime) {
          property.unit = TimeUnit.Day;
        }
        // if it's a list of object, change the property value, name and propertyObjectKey
        if (subType === PropertyType.Object) {
          property.value = propertyValue;
          property.name = propertyName;
          property.propertyObjectKey = propertyObjectKey;
        }
      } finally {
        // if the lookup fails it will just be treated like a string using previous behavior

        this.setState({isLoading: false});
      }
    }

    // Default unknown property to string
    const isUnknownProperty = (itemValue as SectionItemProperty).propertyDefaultType === PropertyType.Unknown;
    if (isUnknownProperty) {
      (itemValue as SectionItemProperty).type = PropertyType.String;
      (itemValue as SectionItemProperty).propertyDefaultType = PropertyType.String;
    }

    // Selectable Item - An item is committed to the Query
    if (this.hasStagedItem()) {
      itemValue = this.applyContextFromStagedItems(itemValue);
    }
    itemValue = menuItemType === MenuItemType.Events ? itemValue.value : itemValue;
    this.selectItem(itemValue, {
      ...detail,
      commit: true,
      onItemClicked: true,
      menuItemType,
    });
  };

  private handleFilterChange = (selectedFilter: FilterValue) => {
    this.resetStage();
    this.setState({selectedFilter});
    this.props.onChangeTab?.(selectedFilter?.dataGroupId || selectedFilter?.resourceType || AllTab);
    this.trackEvent(TRACKING_EVENTS.CHANGED_FILTER, {filter: selectedFilter});
    this.searchInputEl.current?.focus();
  };

  private handleSelectAll = () => {
    const mechanism = this.props.selectAllMechanism;

    if (mechanism === SelectAllMechanism.Partial) {
      // Deselect All except for disabled items
      const selected = this.state.selected.filter((item) => get(this.props.disabledItems, item.value, false));
      this.setState({
        selected,
      });
    }
  };

  private getFilterOptionSectionItemsForSidenav = (): FilterSectionItem[] => {
    if (this.hasStagedItem()) {
      return [];
    }

    const hasAllFilter = !this.props.hideAllPropertiesSelect;
    const hasEvents = this.props.menuItemTypes?.includes(MenuItemType.Events);
    const hasEventProps = this.shouldShowEventProps();
    const hasUserProps = this.shouldShowUserProps();
    const hasCohorts = this.shouldShowCohorts();
    const hasExperiments = this.shouldShowExperiments();
    const visibleDataGroups = (this.getVisibleDataGroups() || []).map((dg) => ({
      shortLabel: `${dg.displayName.toLowerCase()} properties`,
      label: dg.displayName,
      icon: `group-attribute`,
      value: {resourceType: ResourceType.User, dataGroupId: dg.groupID, value: `data-group-${dg.groupID}`},
      tooltip: {
        placement: Placement.Right,
        text: this.t(`query-builder.properties-menu.sidebar-datagroup-tooltip`, {dataGroupName: dg.displayName}),
        size: DisplaySize.Medium,
      },
    }));
    const items = [
      hasAllFilter
        ? {
            ...AllFilter,
            shortLabel: this.t(`query-builder.properties-menu.all-props`),
            label: this.t(`query-builder.properties-menu.sidebar-all`),
            icon: `type-text`,
            tooltip: {
              placement: Placement.Right,
              text: this.t(`query-builder.properties-menu.sidebar-all-tooltip`),
              size: DisplaySize.Medium,
            },
          }
        : null,
      hasEventProps
        ? {
            shortLabel: this.t(`query-builder.properties-menu.event-props`),
            icon: `event`,
            label: this.t(`query-builder.properties-menu.sidebar-event`),
            value: {resourceType: ResourceType.Event, value: `event`},
            tooltip: {
              placement: Placement.Right,
              text: this.t(`query-builder.properties-menu.sidebar-event-tooltip`),
              size: DisplaySize.Medium,
            },
          }
        : null,
      hasUserProps
        ? {
            shortLabel: this.t(`query-builder.properties-menu.profile-props`),
            label: this.t(`query-builder.properties-menu.sidebar-user`),
            icon: `profile-attribute`,
            value: {resourceType: ResourceType.User, value: `user`},
            tooltip: {
              placement: Placement.Right,
              text: this.t(`query-builder.properties-menu.sidebar-user-tooltip`),
              size: DisplaySize.Medium,
            },
          }
        : null,
      /** displays events for defining behavioral filters on the users page */
      hasEvents
        ? {
            icon: `event`,
            label: this.t(`query-builder.properties-menu.sidebar-users-who-did`),
            shortLabel: this.t(`query-builder.properties-menu.sidebar-users-who-did`),
            value: {resourceType: `events` as const, value: `events`},
            tooltip: {
              placement: Placement.Right,
              text: this.t(`query-builder.properties-menu.sidebar-users-who-did-tooltip`),
              size: DisplaySize.Medium,
            },
          }
        : null,
      /** displays the computed section when looking at behaviors  */
      this.getOtherMenuSection().items?.length > 0
        ? {
            icon: `computed`,
            label: this.t(`query-builder.properties-menu.sidebar-computed`),
            value: {resourceType: `computed` as const, value: `computed`},
            tooltip: {
              placement: Placement.Right,
              text: this.t(`query-builder.properties-menu.sidebar-computed-tooltip`),
              size: DisplaySize.Medium,
            },
          }
        : null,
      /** Group Profile Properties */
      ...visibleDataGroups,
      hasCohorts
        ? {
            label: this.t(`query-builder.properties-menu.sidebar-cohorts`),
            icon: `community`,
            value: {resourceType: ResourceType.Cohort, value: `cohorts`},
            tooltip: {
              placement: Placement.Right,
              text: this.t(`query-builder.properties-menu.sidebar-cohorts-tooltip`),
              size: DisplaySize.Medium,
            },
          }
        : null,
      hasExperiments
        ? {
            label: this.t(`query-builder.properties-menu.sidebar-experiments`),
            icon: `experiment`,
            value: {resourceType: `experiments` as const, value: `experiments`},
            tooltip: {
              placement: Placement.Right,
              text: this.t(`query-builder.properties-menu.sidebar-experiments-tooltip`),
              size: DisplaySize.Medium,
            },
          }
        : null,
    ].filter(Boolean);

    /** omit filters when displaying only 2 available categories with one of them being "All" */
    if (items.length === 2 && items[0].value.value === AllFilter.value.value) {
      return [];
    }

    return items.map((option) => ({
      ...option,
      isSelected: option.value.value === this.state.selectedFilter.value,
    }));
  };

  private getCreateSectionItems = () => {
    const sectionItems = [];

    if (this.canCreateCustomEventProperties()) {
      sectionItems.push({
        icon: `event-attribute`,
        label: this.t(`query-builder.properties-menu.create-event-prop`),
        value: SPECIAL_CONTROLS.CREATE_CUSTOM_EVENT_FORMULA.value,
      });
    }

    if (this.canCreateCustomUserProperties()) {
      sectionItems.push({
        icon: `profile-attribute`,
        label: this.t(`query-builder.properties-menu.create-user-prop`),
        value: SPECIAL_CONTROLS.CREATE_CUSTOM_PEOPLE_FORMULA.value,
      });
      if (this.isBehavioralPropertiesEnabled()) {
        sectionItems.push({
          label: this.t(`query-builder.properties-menu.create-behavioral-prop`),
          value: SPECIAL_CONTROLS.CREATE_CUSTOM_BEHAVIORAL_PROPERTY.value,
        });
      }
    }

    if (this.canCreateCustomGroupProperties()) {
      sectionItems.push({
        icon: `profile-attribute`,
        label: this.t(`query-builder.properties-menu.create-group-prop`),
        value: SPECIAL_CONTROLS.CREATE_CUSTOM_PEOPLE_FORMULA.value,
      });
    }

    if (this.canCreateInlineCohorts()) {
      sectionItems.push({
        icon: `community`,
        label: this.t(`query-builder.properties-menu.create-cohort`),
        value: SPECIAL_CONTROLS.CREATE_INLINE_COHORT.value,
      });
    }

    return sectionItems;
  };

  private useMultipleSelection = () =>
    this.props.multiple || (this.props.cohortMultiselect && this.isCohortSelection());

  private getObjectMenuSection = (): PropertySection[] => {
    let property;
    let forExperiments = false;

    // if we want to support unlimited nesting in the future, we can use getParents on the filter to get the whole list tree
    const listOfObjectsProperty = this.props.listProperty;

    if (listOfObjectsProperty) {
      if (listOfObjectsProperty.propertyObjectKey) {
        // if it's a list of objects within an object, use listObjectsProperty as it is
        property = listOfObjectsProperty;
      } else {
        property = {
          resourceType: listOfObjectsProperty.resourceType,
          propertyType: `list_object`,
          propertyName: listOfObjectsProperty.propertyName,
          propertyDefaultType: PropertyType.List,
          ...(listOfObjectsProperty.dataGroupId ? {dataGroupId: listOfObjectsProperty.dataGroupId} : {}),
        };
      }
    } else {
      const [baseProperty, ...nestedProps] = this.filterPropertyStagedItems();

      property = propertyItemToObjectPropertyItem({
        ...baseProperty,
        ...(nestedProps.length
          ? {
              propertyObjectKey: nestedProps.map((nestedProperty) => nestedProperty.value),
              joinPropertyType: PropertyType.Object,
            }
          : {}),
      });

      forExperiments = baseProperty.value === MPProperties.Experiments;
    }

    const client =
      property.resourceType === ResourceType.Event ? this._eventPropertiesClient : this._peoplePropertiesClient;
    const params = {
      [listOfObjectsProperty && !listOfObjectsProperty.propertyObjectKey ? `property` : `objectProperty`]: property,
      projectId: this.sourceAttrs.projectId,
      workspaceId: this.sourceAttrs.workspaceId,
      ...(property.dataGroupId ? {dataGroupId: property.dataGroupId} : {}),
    };

    const properties = client.getCachedResponse(params);

    if (!properties) {
      // Asynchronously fetches or looks up nested object property in cache
      client.query(params, {forceCache: true}).then(() => this.forceUpdate());

      return [
        {
          isLoading: true,
          items: [],
        },
      ];
    }

    const sections: PropertySection[] = [
      {
        items: this.getPropertyMenuItems({
          properties: properties.map((p) => {
            return {
              ...p,
              label: forExperiments ? renamePropertyValue(p.name, MappedPropertyValues.ExperimentId) : p.label,
              type: forExperiments ? PropertyType.String : p.type,
              isSelected: false,
            };
          }),
          resourceType: property.resourceType,
        }),
      },
    ];

    if (listOfObjectsProperty) {
      const computedListPropertiesSection = this.getComputedListProperties();
      if (computedListPropertiesSection) {
        sections.push(computedListPropertiesSection);
      }
    }

    return sections;
  };

  private getGroupMenuSection = (item: PropertySectionItem) => {
    const dataGroupID = item.value.groupId;
    return [
      {
        items: this.getPropertyMenuItems({
          properties: this.state.dataGroups[dataGroupID].properties,
          resourceType: ResourceType.User,
        }),
      },
    ];
  };

  /** renders all properties belonging to a lookup table */
  private getDimensionMenuSection = (item: PropertySectionItem) => {
    assert(isProperty(item));
    const dataGroupId = item.tempDataGroupId || item.dimensionDataGroupId;
    const dimension = item.value;
    const lookupTableProperties = (lookupTablesStore.value[dataGroupId]?.properties || []).map(
      (property: LookupTableProperty): Property => {
        return {
          ...property,
          dataGroupId,
          propertyDefaultType: property.type,
          resourceType: item.resourceType,
          value: [dimension, property.name],
        };
      },
    );

    if (lookupTableProperties.length === 0) {
      reporter.error(`[lookup] properties-menu: blank lookup table shown`, {
        projectId: this.sourceAttrs.projectId,
        workspaceId: this.sourceAttrs.workspaceId,
        userId: this.getUserId(),
        dataGroupId,
      });
    }

    const properties = [
      // Include the dim prop underlying property.
      {
        propertyDefaultType: item.propertyDefaultType,
        resourceType: item.resourceType,
        name: item.name,
        label: item.label,
        type: item.joinPropertyType,
        value: dimension,
        customProperty: item.customProperty,
        customPropertyId: item.customPropertyId,
      },
      ...lookupTableProperties,
    ];

    return [
      {
        items: this.getPropertyMenuItems({
          properties,
          resourceType: item.resourceType,
        }),
      },
    ];
  };

  private getDatetimeMenuSections = () => {
    return [{items: [...DATETIME_UNIT_OPTION_SECTION_ITEMS]}];
  };

  private getTypecastMenuSections = ({type}) => {
    const typeCastTypes = [...TYPECAST_PROPERTY_TYPES, PropertyType.List];
    const types = [...typeCastTypes, ...(typeCastTypes.includes(type) ? [] : [type])];

    const items = types.map((typeCastType) => {
      return {
        type: typeCastType,
        icon: getIconForPropertyType(typeCastType),
        isSelected: typeCastType === type,
        label: getLabelForPropertyType(typeCastType),
        value: typeCastType,
      };
    });

    return [{items}];
  };

  private sectionsFromInput = (input: string, sections: Section[]): Array<Section | CombinedSection> => {
    const manualEntryItem = this.getManualEntryItem(input);

    const refreshItem = this.canRefreshData()
      ? {
          label: this.t(`query-builder.refresh`),
          icon: `refresh`,
          excludeFromInputMatch: true,
          value: SpecialActions.Refresh,
          isSpecialAction: true,
        }
      : null;

    const combinedSectionItems = [manualEntryItem, refreshItem].filter((item): item is PropertySectionItem =>
      Boolean(item),
    );
    if (combinedSectionItems.length === 0) {
      return [];
    }

    const emptyResultsSection =
      manualEntryItem && sections.every((section) => section.items.length === 0)
        ? {
            label: this.t(`query-builder.no-search-results`),
            alwaysShowLabel: true,
            items: [],
          }
        : null;

    return [
      ...(emptyResultsSection ? [emptyResultsSection] : []),
      {
        items: combinedSectionItems,
        type: this.getResourceTypeFilter(),
      } as CombinedSection,
    ];
  };

  private onDeletedCustomProperty = () => {
    const selected = this.state.selected;
    assert(!Array.isArray(selected) && isProperty(selected));
    const selectedCustomPropertyId = selected.customPropertyId;
    // when filter menu is open, `selected` on the underlying
    // properties-menu is set to null, so we have to manually delete
    // ourselves here. the delete callback can handle anything else.
    if (!selectedCustomPropertyId) {
      this.props.onDelete?.();
    }
    this.props.onModalClose?.();
  };

  private applyCustomProperty = (customProp, originalCustomPropId) => {
    // originalCustomPropId will be the same as customProp.customPropertyId
    // unless we are saving a temporary custom property for the first time
    // (in which case it will be the old temporary id) or we are creating
    // a custom property for the first time (in which case it will be null)
    let {resourceType, customProperty, customPropertyId} = customProp;
    // we use 'user' and 'event' in the new query builder
    if (Object.keys(INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING).includes(resourceType)) {
      resourceType = INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING[resourceType];
    }
    customProperty = sanitizeCustomProperty(customProperty);
    const property = {
      ...customProp,
      dataGroupId: getDataGroupIdForCustomProp(customProperty, resourceType),
      label: customProperty.name,
      customProperty,
      customPropertyId: isPersistedCustomProperty(customPropertyId) ? Number(customPropertyId) : customPropertyId,
      resourceType,
    };

    let selected = this.state.selected;
    if (!selected) {
      selected = [];
    } else if (!Array.isArray(selected)) {
      selected = [selected];
    }
    const index = selected.findIndex(
      (selectedItem) =>
        isProperty(selectedItem) &&
        (selectedItem.customPropertyId === property.customPropertyId ||
          selectedItem.customPropertyId === originalCustomPropId),
    );

    const selectedCustomProp = selected[index] as Property;
    const {joinPropertyType} = customProperty;
    if (joinPropertyType) {
      property.joinPropertyType = joinPropertyType;
      if (Array.isArray(selectedCustomProp.value)) {
        // dimension property selected
        property.dataGroupId = customProperty.mappedDataGroupId;
        property.value = [property.value, selectedCustomProp.value[1]];
        property.propertyDefaultType = selectedCustomProp.propertyDefaultType;

        property.type = selectedCustomProp.type;
        property.propertyType = selectedCustomProp.type;
      } else {
        property.type = joinPropertyType;
        property.propertyType = joinPropertyType;
      }
    }

    if (index > -1) {
      this.replaceItem(property, index, {commit: true});
    } else {
      this.selectItem(property, {commit: true});
    }
    this.props.onModalClose?.();
  };

  public trackEvent(eventName, props = {}, {cancelable = false} = {}) {
    const additionalProps = {
      [`usage context`]: TrackingUsageContext.Report,
    };
    if (this.props.trackName) {
      additionalProps[`tab`] = this.props.trackName;
    }
    emitTrackEvent(this.rootEl.current, {
      props: {
        ...props,
        ...additionalProps,
      },
      eventName,
      cancelable,
    });
  }

  public trackSelection({props, ...eventData}, cancelable = true) {
    const additionalProps = {
      [`usage context`]: TrackingUsageContext.Report,
      'Event Names': this.props.eventNames,
    };
    if (this.props.trackName) {
      additionalProps[`tab`] = this.props.trackName;
    }
    emitTrackEvent(this.rootEl.current, {
      ...eventData,
      props: {
        ...props,
        ...additionalProps,
      },
      cancelable,
    });
  }

  canOpenMenu() {
    return !this.props.readonly;
  }

  shouldCloseOnSelect() {
    return !this.shouldShowApplyButton();
  }

  customPropertiesStoreCallback = () => {
    this.getTooltipData.cache.clear();
  };

  generateShowObjectPropertiesItemForKey = ({
    key,
    resourceType,
    dataGroupId,
  }: {
    key: string;
    resourceType: ResourceType;
    dataGroupId?: string;
  }): PropertySectionItem => {
    let menuItemType;
    switch (resourceType) {
      case ResourceType.Event:
        menuItemType = MenuItemType.EventProperties;
        break;
      case ResourceType.User:
        menuItemType = MenuItemType.UserProperties;
        break;
      case ResourceType.Cohort:
      case ResourceType.Other:
      case ResourceType.Metric:
      case ResourceType.Formula:
        throw Error(`Unexpected resource type in generateShowObjectPropertiesItemForKey: ${resourceType}`);
      default:
        assertUnreachable(resourceType);
    }
    return {
      type: PropertyType.Object,
      ...(dataGroupId ? {dataGroupId} : {}),
      propertyDefaultType: PropertyType.Object,
      label: key,
      name: key,
      resourceType,
      value: key,
      stagedItemType: StagedItemType.Object,
      menuItemType,
    };
  };

  get currentProject(): CurrentProject | null {
    return this.props.currentProject.get().value;
  }

  get sourceAttrs() {
    return {
      projectId: Number(this.props.projectId || getProjectId()),
      workspaceId: Number(this.props.workspaceId || getWorkspaceId()),
    };
  }

  get persistence() {
    return this._cache.persistence;
  }

  getRecentItems(itemType) {
    return this._cache?.getRecentItems(itemType, NUM_RECENT_ITEMS) || [];
  }

  addRecentItem(itemType, item) {
    this._cache.addRecentItem(itemType, item, getItemUniqueKey);
  }

  public loadStoreValues() {
    // all properties-menus upload any inline custom properties they host.
    const inlineCustomProperties = flatten(
      this.getPropertyResourceTypes().map((type) => {
        return this.getSelectedCustomFormula(type);
      }),
    ).reduce((acc, cur) => {
      assert(isProperty(cur));
      const {customPropertyId, customProperty} = cur;
      return {
        ...acc,
        [getInlineCustomPropertyId(customPropertyId)]: customProperty,
      };
    }, {});

    customPropertiesStore.update(inlineCustomProperties);

    // load all inline cohorts into cohort store
    cohortsStore.update(
      this.getInlineCohortItems().reduce((acc, cur) => {
        const cohort: SerializedInlineCohort = {
          name: cur.label,
          created_by: cur.createdBy,
          raw_cohort: cur.value.cohort.raw_cohort,
        };
        return {
          ...acc,
          [cur.value.cohort.raw_cohort.unsavedId]: cohort,
        };
      }, {}),
    );
  }

  public resetStage() {
    this.setState({
      stagedItemsStack: [],
      stagedItemType: null,
    });
  }

  public hasStagedItem(): boolean {
    return this.state.stagedItemsStack?.length > 0;
  }

  public peekStagedItem() {
    return this.state.stagedItemsStack[this.state.stagedItemsStack.length - 1];
  }

  /**
   * Updates the selected filter to be Cohorts if there's an committed cohort selection
   */
  public updateFromSelection() {
    let selected: Selection = this.state.selected || [];
    selected = Array.isArray(selected) ? selected : [selected];
    const singleSelection = selected[0];

    const filterOptions = this.getFilterOptionSectionItemsForSidenav().map((item) => ({
      ...omit(item, `icon`, `shortLabel`),
      label: item.shortLabel,
    }));
    const defaultFilter = filterOptions[0]?.value ?? AllFilter.value;
    let selectedFilter: FilterValue = AllFilter.value;

    if (singleSelection) {
      const isCohortSelection = Array.isArray(this.state.selected) && isCohort(singleSelection);

      if (this.props.hideAllPropertiesSelect) {
        selectedFilter = defaultFilter;
      } else if (isCohortSelection && this.shouldShowCohorts()) {
        selectedFilter = {resourceType: ResourceType.Cohort, value: `cohorts`};
      }
    }

    this.setState({selectedFilter});
    if (this.state.selectedFilter?.value !== selectedFilter?.value) {
      this.props.onChangeTab?.(selectedFilter.value);
    }
  }

  getUserId() {
    return this.props.userId || userCookieUtils.getUserIdentityFromCookie()?.id;
  }

  maybeNavigateToSelected() {
    const selected = this.state.selected;
    const selectedItem = this.getSelectedItem();
    if (!selectedItem) {
      return;
    }

    if (this.isDimensionSelected(selected) && this.canStageItemType(StagedItemType.Dimension)) {
      // open dimension property submenu
      const property = this.getLookupTablePropertyFromSelected(selectedItem);
      if (property) {
        const item = this.getPropertyMenuItem({
          property,
          resourceType: selectedItem.resourceType,
          isDisplayingMultipleResources: this.isDisplayingMultipleResources(),
        });
        this.updateStagedItem(item, StagedItemType.Dimension);
      }
    }

    // NOTE: This block of code that adds the borrow event and borrowed property. This needs to be before the
    // block further down that add screens for nested object properties.
    if (`borrowEvent` in selectedItem) {
      this.updateStagedItem(BORROW_PROPERTY, StagedItemType.BorrowEvent);
      const stagedEventItem: EventSectionItem = {
        label: null,
        value: selectedItem.borrowEvent,
        menuItemType: MenuItemType.EventWithProperties,
      };
      this.updateStagedItem(stagedEventItem, StagedItemType.PropertiesForEvent);
    }

    // Unfurl/restore nested object properties to stagedItemsStack
    if (
      isProperty(selectedItem) &&
      selectedItem?.joinPropertyType === PropertyType.Object &&
      selectedItem?.propertyObjectKey?.length
    ) {
      [selectedItem.value, ...selectedItem.propertyObjectKey.slice(0, -1)].forEach((propertyKey) => {
        const propertyKeyString = Array.isArray(propertyKey) ? propertyKey[propertyKey.length - 1] : propertyKey;
        this.updateStagedItem(
          this.generateShowObjectPropertiesItemForKey({
            key: propertyKeyString,
            resourceType: selectedItem.resourceType,
            dataGroupId: selectedItem.dataGroupId,
          }),
          StagedItemType.Object,
        );
      });
    } else if (isProperty(selectedItem) && selectedItem.behavior) {
      this.navigateToBehavior(selectedItem.behavior);
    } else if (
      this.props.listProperty &&
      isProperty(selectedItem) &&
      typeof selectedItem.value === `string` &&
      [LIST_PROPERTY_SUM.value, LIST_PROPERTY_UNIQUES.value].includes(selectedItem.value)
    ) {
      const stagedItem = selectedItem.value === LIST_PROPERTY_SUM.value ? LIST_PROPERTY_SUM : LIST_PROPERTY_UNIQUES;
      this.updateStagedItem(stagedItem, stagedItem.stagedItemType);
    }
  }

  navigateToBehavior(behavior: Behavior) {
    const behaviorItem = getMenuItemForBehavior(behavior);
    const type = isMultiAttributionBehavior(behavior)
      ? StagedItemType.BehaviorMultiAttribution
      : StagedItemType.BehaviorWithEvent;
    if (behaviorItem.stagedItemType) {
      this.updateStagedItem(behaviorItem, type);
    }
  }

  /**
   * This allows us to set the open time in an exhaustive-yet-safe manner.
   * We cannot rely on one single code path to signal a menu's first open.
   *  1) attributeCallback triggers are unordered
   *  2) It is unknown if a menu will be open on connectedCallback
   */
  private setOpenedAt(open: boolean) {
    // setOpenedAt may be called before this.attr managed state is available.
    if (open) {
      if (!this._openedAt) {
        // Only track first menu open time
        this._openedAt = getNow();
        this._openCount += 1;

        let openedComponent: OpenedMenuComponent;
        captureWorkflowTiming(this, {
          name: `Properties Menu`,
          key: `properties-menu`,
          isComplete: (ev) => {
            // todo: replace this shit, absolutely no way it works
            openedComponent = getOpenedMenuComponent(ev, this.rootEl.current);
            return Boolean(openedComponent);
          },
          additionalProps: () => ({
            openCount: this._openCount,
            openedComponent,
          }),
        });
        captureWorkflowTiming(this, {
          name: `Properties Menu Loaded`,
          key: `properties-menu-loaded`,
          // todo: this too
          isComplete: (ev) => getOpenedMenuComponent(ev, this.rootEl.current) === OpenedMenuComponent.ItemsMenu,
          additionalProps: () => ({
            numTotalItems: this._numTotalItemsOnPrevRender,
            openCount: this._openCount,
          }),
        });
      }
    } else {
      // unset time when menu closed
      this._openedAt = null;
    }
  }

  public isCohortSelection() {
    return this.state.selectedFilter?.resourceType === ResourceType.Cohort;
  }

  public getStagedItemType(): StagedItemType {
    if (!this.hasStagedItem() && this.state.stagedItemType) {
      // Ensure there a stack before returning type as stagedItem and stagedItemType can get out of sync.
      // Out use of stagedItemType will be refactored out; Until then, warn on any mismatch.
      reporter.error(`StagedItemType<${this.state.stagedItemType}> exists when there is not StagedItemStack`);
      return null;
    }
    return this.state.stagedItemType;
  }

  public getPrefixLabel() {
    const selected = this.state.selected;
    if (!selected) {
      return null;
    }

    if (`behavior` in selected) {
      return getPrefixForBehavior(selected.behavior);
    } else if (!Array.isArray(selected) && selected.resourceType !== ResourceType.Cohort) {
      return getPropertyPathPrefix(selected, this.props.i18n, Object.values(dataGroupStore.value), {
        includeResourceLabel: !this.props.listProperty && queryBuilderUtils.isCoreReport(),
      });
    }

    return null;
  }

  public selectedLabel() {
    return getSelectedLabel({
      selected: this.props.selected,
      cohortsStore,
      showSpecialDatePropertyUnit: this.props.showSpecialDatePropertyUnit,
      dataGroups: Object.values(this.state.dataGroups),
      expandListProperties: false,
    });
  }

  public selectedLeftIcon() {
    return getSelectedLeftIcon(this.state.selected || []);
  }

  private async clickEditCustomProperty(property) {
    const {appliedValue, deletedId} = await this.promptCustomPropertiesModal(property);
    if (appliedValue) {
      this.applyCustomProperty(appliedValue, property.customPropertyId);
      this.trackEvent(TrackingEvent.EditCustomProperty, {
        context: this.props.context,
        name: property.name,
        resourceType: property.resourceType,
      });
    }
    if (deletedId) {
      let selected = this.state.selected;
      if (!selected) {
        selected = [];
      } else if (!Array.isArray(selected)) {
        selected = [selected];
      }
      const index = selected.findIndex(
        (selectedItem) => isProperty(selectedItem) && selectedItem.customPropertyId?.toString() === deletedId,
      );
      this.removeItem(index);
    }
  }

  private handleItemPillClick(item) {
    if (item.pillAction === PropertyItemAction.Typecast) {
      const action = this.props.allowTypecastPillAction;
      switch (action) {
        case TypecastPillAction.Typecast:
          this.updateStagedItem(item, StagedItemType.Typecast);
          break;
        case TypecastPillAction.TriggerTutorial:
          this.selectItem(item, {commit: true});
          break;
        default:
          assertUnreachable(action);
      }
    } else {
      console.warn(`Invalid primary action for property item`);
    }
  }

  private updateStagedItem(stagedItem: EventOrPropertySectionItem, stagedItemType: StagedItemType) {
    let updatedStack = [];
    let updatedStagedItemType = stagedItemType;

    this.setState((prevStates) => {
      if (stagedItem === null) {
        // Immutable pop
        updatedStack = [...prevStates.stagedItemsStack].slice(0, -1);
        const itemToBeStaged: PropertySectionItem = updatedStack[updatedStack.length - 1];
        if (updatedStack.length > 0) {
          updatedStagedItemType =
            inferStagedItemType(itemToBeStaged, this.useMultipleSelection()) ?? prevStates.stagedItemType;
        } else {
          updatedStagedItemType = null;
        }
      } else if (stagedItem && `path` in stagedItem && stagedItem.path?.length) {
        updatedStack = [...stagedItem.path, stagedItem];
      } else {
        updatedStack = [...prevStates.stagedItemsStack, stagedItem];
      }
      return {
        ...prevStates,
        stagedItemsStack: updatedStack,
        stagedItemType: updatedStagedItemType,
        searchFilter: ``,
      };
    });

    // If we staged an event we need to fetch props for the event
    if (stagedItemType === StagedItemType.PropertiesForEvent) {
      // TODO should we include dimension properties?
      // TODO load state management
      const includeDimensionProperties = !this.props.excludeDimensionProperties;
      this.setState({loadingEventProperties: true});
      fetchPropertyDefinitionsForEvents([stagedItem.value.value], {includeDimensionProperties}).then(() =>
        this.eventPropertiesDefinitionsStoreCallback(),
      );
    }

    this.props.onStagedProperty?.(updatedStack);

    requestAnimationFrame(() => {
      // set browser focus to search input
      this.searchInputEl.current?.focus();
    });
  }

  showCreateItemModal(value: SpecialActions) {
    switch (value) {
      case SpecialActions.CreateCustomEventFormula:
        return this.createCustomProperty(ResourceType.Event, false);
      case SpecialActions.CreateCustomPeopleFormula:
        return this.createCustomProperty(ResourceType.User, false);
      case SpecialActions.CreateCustomBehavioralProperties:
        return this.createCustomProperty(ResourceType.Event, true);
      case SpecialActions.CreateInlineCohort:
        return this.createInlineCohort();
      case SpecialActions.NavigateBack:
      case SpecialActions.Refresh:
        throw new Error(`Unsupported action`);
      default:
        assertUnreachable(value);
    }
  }

  handleExpandItemClick({detail: {section, sectionIdx}}: ExpanderItemClickEvent) {
    // todo: fix this for i18n
    if (section.label?.toString().includes(`Hidden`)) {
      this.trackEvent(TrackingEvent.ShowHiddenEntity);
    } else {
      this.trackEvent(TrackingEvent.ExpandedSection, {
        context: this.props.context,
        'Number of items in section': section.items.length,
        'Search - filter': this.state.searchFilter,
        'Section index': sectionIdx,
      });
    }
  }

  private handleCohortUpdate(cohortStagedValue, cohortSavedValue) {
    if (cohortStagedValue || cohortSavedValue) {
      const cohortValue = cohortStagedValue || cohortSavedValue;
      const createdBy = this.getCreatedBy();
      const {commitedItem, isSaved, unsavedId, isEditingTemporaryCohort, isEditingExistingCohort} = commitCohort(
        cohortsStore,
        createdBy,
        cohortValue,
      );

      this.replaceOrSelectCohort(commitedItem, isEditingTemporaryCohort, unsavedId);
      this.trackInlineCohorts(name, isSaved, isEditingTemporaryCohort, isEditingExistingCohort, cohortValue.savedAsNew);
    }
    this.props.onModalClose?.();
  }

  showBorrowedProperties(): boolean {
    return (
      this.props.showBorrowedProperties &&
      // using a borrowed property as the attribution by property is not supported
      this.getStagedItemType() !== StagedItemType.BehaviorMultiAttribution
    );
  }

  private promptCustomPropertiesModal(property, forBehavioralProperty = false) {
    const eventNames =
      this.getStagedItemType() === StagedItemType.BehaviorMultiAttribution
        ? this.props.eventNames.concat([SPECIAL_EVENTS.ANY_EVENT])
        : this.props.eventNames;

    const entityNames = this.getCustomEntities([ResourceType.Event, ResourceType.User]).map((item) => item.value);
    let defaultName = ``;
    for (let i = 1; ; i++) {
      defaultName = this.props.cohortOperationType
        ? `Custom ${this.props.cohortOperationType} ${i}`
        : this.t(`custom-property.name-placeholder`, {number: i});
      if (!entityNames.includes(defaultName)) {
        break;
      }
    }

    // can use getSingleDataGroupId() here because create > Custom Group Property option is disabled for multi data group queries
    const dataGroupId = getSingleDataGroupId(this.props.dataGroupIds);

    const attrs: CustomPropertyBuilderAttrs = {
      'all-data-groups': property.resourceType !== ResourceType.User || Boolean(dataGroupId),
      'data-group-id': dataGroupId ?? ``,
      'default-name': defaultName,
      'behavioral-properties-mode': forBehavioralProperty,
      'event-names': eventNames,
      'enable-workspace-filter-restrictions': this.props.enableWorkspaceFilterRestrictions,
      'show-borrowed-properties': this.showBorrowedProperties(),
      open: true,
      'project-id': this.props.projectId,
      'user-id': this.props.userId,
      property: JSON.stringify(property) as Partial<Property>,
      'track-name': this.props.trackName,
      'workspace-id': this.sourceAttrs.workspaceId,
    };
    this.props.onModalOpen?.();
    return promptCustomProperties(attrs);
  }

  private promptInlineCohortsModal(cohort: CohortModel) {
    const props: InlineCohortsModalProps = {
      canSave: this.props.projectHasCohorts,
      canEdit: isAllowedSuperadminOverride(cohort) || cohort.can_update_basic,
      // ok to use getSingleDataGroupId because create cohort option is hidden for multi data group queries
      dataGroupId: getSingleDataGroupId(this.props.dataGroupIds) ?? null,
      showDataGroupsSelector: this.props.metricLevelDataGroups,
      canApplyUnsavedCohort: !cohort?.id,
      name: cohort?.name,
      open: true,
      projectHasCohorts: this.props.projectHasCohorts,
      supportB2bCohorts: this.props.supportB2bCohorts,
      projectId: this.sourceAttrs.projectId,
      cohort: cohort?.serialize(),
      trackName: this.props.trackName,
      userId: this.getUserId(),
      workspaceId: this.sourceAttrs.workspaceId,
      showCohortCount: true,
    };
    this.props.onModalOpen?.();
    return promptInlineCohorts(props);
  }

  private addSelectedItemToRecent(item) {
    if (this.isInlineCohort(item) || (item.customProperty && !isPersistedCustomProperty(item.customPropertyId))) {
      return;
    }

    this.addRecentItem(item.resourceType, {
      ...pick(item, PropertyItemPersistedIdentifierKeys),
      [RECENT_ITEM_TIMESTAMP_KEY]: Date.now(),
    });
  }

  private selectItem(
    item: EventSectionItem | PropertySectionItem,
    {
      // Was an item in the properties menu clicked?
      onItemClicked = false,
      // Indicates whether the item selection is user triggered or automatically triggered (e.g.
      // custom properties / cohorts store callback). Note that this is not just the inverse of
      // onItemClicked. For example, if a user is creating a custom property via. the modal
      // autoTriggered will be false, and onItemClicked will also be false (since an item wasn't
      // clicked in the menu)
      autoTriggered = false,
      commit = false,
      searchFilter,
      labelMatch,
      subLabelMatch,
      customMatch,
      menuItemType,
    }: {
      onItemClicked?: boolean;
      autoTriggered?: boolean;
      commit?: boolean;
      searchFilter?: string;
      labelMatch?: SearchMatchResult;
      subLabelMatch?: SearchMatchResult;
      customMatch?: SearchMatchResult;
      menuItemType?: MenuItemType;
    } = {},
  ) {
    const propertySectionItem = item as PropertySectionItem;
    const isDimensionProperty = isProperty(propertySectionItem) && propertySectionItem.type === PropertyType.Dimension;
    if (autoTriggered && isDimensionProperty && !this.props.excludeLookupTableProperties) {
      return;
    }

    let bookmarkableSelection;
    if (menuItemType === MenuItemType.Events) {
      // events-menu + properties-menu are mixing types for events. Force the property type until these are consolidated
      bookmarkableSelection = filterToBookmarkableEventItem(item as EventItem);

      this.addRecentItem(ResourceType.Event, {
        ...item,
        [RECENT_ITEM_TIMESTAMP_KEY]: Date.now(),
      });
    } else {
      bookmarkableSelection = filterToBookmarkablePropertyItem(
        pickBy(item, (value) => value !== undefined) as PropertySectionItem,
      );
      this.addSelectedItemToRecent(bookmarkableSelection);
    }

    if (this.useMultipleSelection()) {
      // if item was not removed, add it to the selection
      const baseSelection = this.getArraySelection();
      const selectionArray = baseSelection.filter((selectedItem) =>
        selectedItem.value &&
        isInlineCohort(selectedItem) &&
        isSerializedInlineCohort(selectedItem.value.cohort) &&
        isSerializedInlineCohort(item.value.cohort)
          ? !isEqual(selectedItem.value.cohort.raw_cohort, item.value.cohort.raw_cohort)
          : selectedItem.value !== item.value,
      );
      if (baseSelection.length === selectionArray.length) {
        selectionArray.push(bookmarkableSelection);
      }
      this.setState({
        selected: selectionArray,
      });
    } else {
      this.props.onSelect?.({
        item: bookmarkableSelection,
        rawItem: propertySectionItem,
        onItemClicked,
        autoTriggered,
        commit,
        menuItemType,
        stagedItemsStack: this.state.stagedItemsStack,
      });
    }

    if (onItemClicked) {
      const propSelectionItem = item as PropertySectionItem;
      this.trackSelection(
        {
          eventName: TrackingEvent.SelectProperty,
          props: {
            'item type': menuItemType,
            'Selected label': item.label,
            'Selected value': item.value,
            'Selected resource type': propSelectionItem.resourceType,
            'is custom property': isProperty(propSelectionItem) && !!propSelectionItem.customProperty,
            'is hidden entity': !!propSelectionItem.hidden,
            'is recently viewed': !!propSelectionItem.isRecentlyUsed,
            propertyDefaultType: isProperty(propSelectionItem) && propSelectionItem.propertyDefaultType,
            propertyType: propSelectionItem.propertyType,
            'Search - filter': searchFilter,
            'Search - label match': labelMatch,
            'Search - sublabel match': subLabelMatch,
            'Search - custom match': customMatch,
            'Staged items': this.state.stagedItemsStack.map((stagedItem) => stagedItem.label),
            'Elapsed time (ms) since menu opened': getNow() - this._openedAt,
            unit: propSelectionItem.unit,
          },
        },
        false, // Making this not cancelable so it's always tracked. https://mixpanel.slack.com/archives/C0KN5EVB8/p1604641156158700
      );
    }
  }

  private replaceItem(
    item,
    index,
    {
      onItemClicked = false,
      commit = false,
      autoTriggered = false,
    }: {onItemClicked?: boolean; commit?: boolean; autoTriggered?: boolean} = {},
  ) {
    // Pickout undefined values to prevent duplicate actions triggering
    const selection = filterToBookmarkablePropertyItem(
      pickBy(item, (value) => value !== undefined) as PropertySectionItem,
    );

    this.addSelectedItemToRecent(selection);

    let newSelection = selection;

    if (this.useMultipleSelection()) {
      // if item was not removed, add it to the selection
      newSelection = replaceByIndex(this.getArraySelection(), index, selection as any);

      this.setState({
        selected: newSelection,
      });
    }

    this.props.onSelect?.({
      item: newSelection,
      rawItem: item,
      commit,
      autoTriggered,
      stagedItemsStack: this.state.stagedItemsStack,
    });

    if (onItemClicked) {
      this.trackSelection({
        eventName: TrackingEvent.SelectProperty,
        props: {},
      });
    }
  }

  private removeItem(index) {
    if (this.useMultipleSelection()) {
      const selection = [...this.getArraySelection()];
      selection.splice(index, 1);

      this.setState({
        selected: selection,
      });
    }
  }

  /** filter to only property screens (e.g. normal property, nested object properties) */
  filterPropertyStagedItems(): PropertySectionItem[] {
    return this.state.stagedItemsStack.filter((item): item is PropertySectionItem => {
      return (
        // filter out non-property items
        !isEventSectionItem(item) &&
        item.menuItemType !== MenuItemType.BorrowProperty &&
        isProperty(item) &&
        !isNil(item.type)
      );
    });
  }

  /**
   * Updates the SectionItem object with necessary information from the stagedItems stack.
   * TODO: Ideas to make this a bit cleaner
   *   TODO- This method currently mutates the item. While there are no current known issues, we should be more protective.
   *   TODO- Look into adding this information when and item being staged versus when it is being selected.
   */
  private applyContextFromStagedItems(item) {
    // TODO add typing (this should be PropertySectionItem)
    const stagedItemType = this.getStagedItemType();
    const originalStagedItemsStack = [...this.state.stagedItemsStack];

    const [rootStagedItem, ...stagedPropertyItems] = this.filterPropertyStagedItems();
    const stagedItem = this.peekStagedItem();

    let itemValue = item;
    // TODO: this logic seems brittle. We should reconsider this design.
    if (
      ![
        StagedItemType.ComputedListProperty,
        StagedItemType.ComputedListNumericProperty,
        StagedItemType.BehaviorWithEvent,
        StagedItemType.BehaviorMultiAttribution,
        StagedItemType.Object,
        StagedItemType.User,
        StagedItemType.Group,
        StagedItemType.Cohort,
      ].includes(stagedItemType)
    ) {
      this.resetStage();
    }

    let baseObjectProps: TODO = null;
    if (isProperty(rootStagedItem) && rootStagedItem.type === PropertyType.Object && stagedPropertyItems.length) {
      const stagedPropertyItem = stagedItem as PropertySectionItem;
      baseObjectProps = {
        ...rootStagedItem,
        propertyObjectKey: stagedPropertyItems.map((property) => property.value),
        joinPropertyType: rootStagedItem.type,
        propertyDefaultType: isProperty(stagedPropertyItem) && stagedPropertyItem.type,
        type: stagedPropertyItem.type,
      };
    }

    if (stagedItemType === StagedItemType.Typecast) {
      itemValue = {
        ...(baseObjectProps ?? stagedItem),
        // for custom property menu, staged item is typecast because we do not show date unit selector
        // so we pass in the unit from handleClickItem directly
        ...(item.unit ? {unit: item.unit} : {}),
        type: item.value,
      };
    } else if (stagedItemType === StagedItemType.Datetime) {
      itemValue = {
        ...(baseObjectProps ?? stagedItem),
        unit: item.value,
      };
    } else if (stagedItemType === StagedItemType.Dimension) {
      const stagedPropertyItem = stagedItem as PropertySectionItem;
      itemValue = {
        ...omit(stagedPropertyItem, `dataGroupId`),
        label: stagedItem.label,
        value: item.value,
        type: item.type,
        propertyDefaultType: item.type,
        joinPropertyType: isProperty(stagedPropertyItem) && stagedPropertyItem.joinPropertyType,
        ...(stagedPropertyItem.dimensionDataGroupId && Array.isArray(item.value)
          ? {dataGroupId: stagedPropertyItem.dimensionDataGroupId}
          : {}),
        ...(isProperty(stagedPropertyItem) && stagedPropertyItem.tempDataGroupId && Array.isArray(item.value)
          ? {tempDataGroupId: stagedPropertyItem.tempDataGroupId}
          : {tempDataGroupId: undefined}),
      };
    } else if (stagedItemType === StagedItemType.Object && item.type !== PropertyType.Object) {
      this.resetStage();
      itemValue = {
        ...rootStagedItem,
        type: item.type,
        propertyObjectKey: [...stagedPropertyItems, item].map((property) => property.value),
        joinPropertyType: PropertyType.Object,
        propertyDefaultType: item.type,
      };
    } else if (
      [StagedItemType.ComputedListProperty, StagedItemType.ComputedListNumericProperty].includes(stagedItemType)
    ) {
      this.resetStage();
      itemValue = {
        ...pick(rootStagedItem, `type`, `propertyDefaultType`, `value`, `menuItemType`, `propertyDefaultType`),
        resourceType: this.props.listProperty.resourceType,
        propertyObjectKey: [...stagedPropertyItems, item].map((property) => property.value),
      };
    }

    if (originalStagedItemsStack[0]?.menuItemType === MenuItemType.BorrowProperty) {
      this.resetStage();
      itemValue = {
        ...itemValue,
        borrowEvent: originalStagedItemsStack[1].value,
      };
    }

    return itemValue;
  }

  private async fetchData({refresh = false}: {refresh?: boolean} = {}) {
    this.setState({isLoading: true});
    await Promise.all([
      this.fetchGlobalEventProperties({refresh}),
      this.fetchEventProperties({refresh}),
      this.fetchUserProperties({refresh}),
      this.loadDataGroups(),
      this.fetchUserDetails(),
    ]);
    this.setState({isLoading: false});
  }

  private getPropertyResourceTypes() {
    return compact([
      this.canAccessUserProperties() ? ResourceType.User : null,
      this.shouldShowEventProps() ? ResourceType.Event : null,
    ]);
  }

  userPropertiesDefinitionsStoreCallback = () => {
    this.setState({loadingPeopleProperties: false});
    this.getTooltipData.cache.clear();
  };

  private async fetchUserProperties({refresh = false}: {refresh?: boolean} = {}) {
    if (this.canAccessUserProperties()) {
      peoplePropertyDefinitionsStore.unsubscribeAll(this.userPropertiesDefinitionsStoreCallback);
      this.setState({loadingPeopleProperties: true});
      await fetchPropertyDefinitionsForUserProfiles({
        refresh,
        includeCustom: this.props.includeCustomProperties !== IncludeCustomPropertyType.None,
        callback: this.userPropertiesDefinitionsStoreCallback,
      });
    }
  }

  private getTooltipData = memoize(this._getTooltipData, (i, r, o) => stableStringify({i, r, o}));
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private _getTooltipData(
    item: PropertySectionItem | Cohort | Selection,
    resourceType: ResourceType,
    {fromSelected = false} = {},
  ): ContextPanelData {
    const allowDescriptionEdit = !this.props.descriptionEditDisabled;

    let tooltipData = null;
    if (resourceType === ResourceType.Cohort) {
      tooltipData = getTooltipDataForCohort(item as Cohort, {allowDescriptionEdit});
    } else {
      const propertyDefinitions = getPropertyDefinitions(compact([ResourceType.User, ResourceType.Event]));
      const definitions = propertyDefinitions[resourceType];
      const shouldAllowDescriptionEdit = !this.props.descriptionEditDisabled && this.canWriteDataDefinition();

      if (Object.prototype.hasOwnProperty.call(propertyDefinitions, resourceType)) {
        let tooltipArgs;

        if (fromSelected) {
          const selectedProperty = item as Selection;
          assert(!Array.isArray(selectedProperty) && isProperty(selectedProperty));
          const dataGroupId = selectedProperty.dataGroupId || selectedProperty.tempDataGroupId;
          const dataGroupName = this.state.dataGroups[dataGroupId]?.displayName;
          tooltipArgs = {
            name: getTooltipNameForPropertyItem(selectedProperty),
            path: getTooltipPathForPropertyItem(selectedProperty, this.state.dataGroups[dataGroupId], this.props.i18n),
            resourceType: selectedProperty.resourceType,
            dataType: selectedProperty.type,
            dataGroupId,
            dataGroupName,
            customPropertyId: selectedProperty.customPropertyId,
            isLookupTableProperty: !!selectedProperty.tempDataGroupId,
            isRecentlyUsed: this.isPropertyRecentlyUsed(selectedProperty),
            i18n: this.props.i18n,
          };
        } else {
          const propertySectionItem = item as PropertySectionItem;
          assert(isProperty(propertySectionItem));
          const {dataGroupId} = propertySectionItem;
          const dataGroupName = this.state.dataGroups[dataGroupId]?.displayName;
          tooltipArgs = {
            name: getTooltipNameForPropertyItem(propertySectionItem),
            path: getTooltipPathForPropertyItem(
              propertySectionItem,
              this.state.dataGroups[dataGroupId],
              this.props.i18n,
            ),
            resourceType: propertySectionItem.resourceType,
            dataType: propertySectionItem.type,
            dataGroupId,
            dataGroupName,
            customPropertyId: propertySectionItem.customPropertyId,
            isLookupTableProperty: propertySectionItem.isLookupTableProperty,
            isRecentlyUsed: this.isPropertyRecentlyUsed(propertySectionItem),
            i18n: this.props.i18n,
          };
        }

        return getTooltipDataForProperty(tooltipArgs, definitions, {
          allowEdit: !this.props.hideEditControls && !this.props.readonly,
          allowDescriptionEdit: shouldAllowDescriptionEdit,
          lookupTables: lookupTablesStore.value,
          iconReferenceType: `dataType`,
        });
      }
    }
    return tooltipData;
  }

  private getSelectedMenuItem(): PropertySectionItem {
    const selected = this.getSelectedItem();
    // TODO(j) maybe we can just use selected.dataGroupId
    const dataGroupId = getSingleDataGroupId(this.props.dataGroupIds) || (selected as any)?.dataGroupId;

    return (
      isProperty(selected) && selected.behavior
        ? getMenuItemForBehavior(selected.behavior, false, dataGroupId, Object.values(this.state.dataGroups))
        : selected
    ) as PropertySectionItem;
  }

  private getSelectedItem(): Item {
    const selected = this.state.selected;
    return Array.isArray(selected) ? selected[0] : selected;
  }

  private getSelectedResourceType(): ResourceType {
    const selectedItem = this.getSelectedItem();
    if (!selectedItem) {
      return null;
    }
    // TODO: Cohort query entries in Insights are missing resourceType pending
    // https://github.com/mixpanel/analytics/pull/27358, so assume Cohort in this case for now.
    // Remove this when not needed anymore.
    return selectedItem.resourceType || ResourceType.Cohort;
  }

  public getSelectedHoverTooltipData(): ContextPanelData {
    const selected = this.state.selected;
    if (!selected || (Array.isArray(selected) && selected.length > 1)) {
      return null;
    }

    const selectedItem = this.getSelectedItem();
    const resourceType = this.getSelectedResourceType();
    const selectedMenuItem = this.getSelectedMenuItem();
    const value = selectedMenuItem.value;
    const selectedItemTooltipData =
      !Array.isArray(selected) && !Array.isArray(value) && isProperty(selected) && selectedMenuItem.tooltipData;

    if (selectedItemTooltipData) {
      return selectedItemTooltipData;
    } else if (isProperty(selectedItem)) {
      const properties = resourceType === ResourceType.Event ? this.getEventProperties() : this.getUserProperties();
      const property = properties.find((p) => p.name === value);
      if (property && this.isPropertyDisabled(property, value)) {
        return null;
      }
      return this.getTooltipData(selectedItem, resourceType, {fromSelected: true});
    } else if (isCohort(selectedItem)) {
      const cohortId = isSavedCohort(selectedItem) ? value : value.cohort.raw_cohort.unsavedId;
      const cohort = cohortsStore.value[cohortId];
      const cohortItem = cohort ? transformCohortToCohortItem(cohortsStore.value[cohortId], cohortsStore) : null;
      return cohortItem ? this.getTooltipData(cohortItem, resourceType) : null;
    }

    return null;
  }

  public getSelectedTrackingProps(): Obj {
    const selectedItem = this.getSelectedItem();
    if (!selectedItem) {
      return null;
    }

    const props = {};
    const resourceType = this.getSelectedResourceType();
    if (isCohort(selectedItem)) {
      props[`Entity type`] = `cohort`;
      if (selectedItem.value && !Number.isInteger(selectedItem.value)) {
        props[`Is inline`] = true;
      }
    } else {
      props[`Entity type`] = `property`;
      props[`Resource type`] = resourceType;
      if (selectedItem.customPropertyId) {
        props[`Is custom`] = true;
        if (!isPersistedCustomProperty(selectedItem.customPropertyId)) {
          props[`Is inline`] = true;
        }
      }
    }
    return props;
  }

  isInlineCohort(item: PropertySectionItem) {
    return Boolean(item.value?.cohort?.raw_cohort);
  }

  handleTooltipContextAction(ev): TooltipContextActionResult {
    const {name, item} = ev.detail;
    const isCustomProperty = name === ActionType.EditCustomProperty;
    const isCohortOnFeatureFlag = name === ActionType.EditCohort;
    const isCohortInline = this.isInlineCohort(item);

    if (isCustomProperty || isCohortInline || isCohortOnFeatureFlag) {
      if (isCustomProperty) {
        this.clickEditCustomProperty(item.property);
      } else {
        this.editCohort(item);
      }

      ev.stopPropagation();
      return {handled: true, openedModal: true};
    } else if ([ActionType.ShareCohort, ActionType.ShareCustomProperty].includes(name)) {
      this.handleShareTooltipContextAction(ev);
    } else if ((name === ActionType.ViewEntity || name === ActionType.LookupTableInfo) && item.url) {
      window.open(item.url, `_blank`);
      return {handled: true, openedModal: false};
    }
    return null;
  }

  handleShareTooltipContextAction(ev) {
    const {name, item} = ev.detail;
    const isShareCohort = name === ActionType.ShareCohort;

    const {eventName, props} = shareTrack.shareButtonClick(ShareButtonUISource.ContextPanel);
    this.trackEvent(`[${shareEventPrefix}] ${eventName}`, props);
    promptSharing({
      type: isShareCohort ? SharableEntityType.Cohort : SharableEntityType.CustomProperty,
      id: isShareCohort ? item.id : item.property.customPropertyId,
      'project-id': this.sourceAttrs.projectId,
      'is-superadmin': item.superadminOverrideShare,
    });
  }

  canRefreshData() {
    return (
      !this.hasStagedItem() && (this.state.selectedFilter.value === AllFilter.value.value || this.canAddManualEntry())
    );
  }

  canAddManualEntry() {
    return (
      this.state.selectedFilter.value === AllTab ||
      this.state.selectedFilter.resourceType === EventsInPropertiesMenuTab ||
      this.state.selectedFilter.resourceType === ResourceType.Event ||
      (this.state.selectedFilter.resourceType === ResourceType.User &&
        this.state.selectedFilter.dataGroupId === undefined)
    );
  }

  getResourceTypeFilter() {
    return this.state.selectedFilter.resourceType;
  }

  getManualEntryItem(input: string): SectionItem | null {
    if (!this.canAddManualEntry()) {
      return null;
    }

    const resourceTypeFilter = this.getResourceTypeFilter();
    let manualEntryItem = null;
    const label = `"${input}"`;
    switch (resourceTypeFilter) {
      // adds raw event names for selection
      case EventsInPropertiesMenuTab:
        manualEntryItem = {
          value: {value: input, label: input},
          label,
          itemFromInput: true,
        };
        break;
      // adds a raw event property
      case ResourceType.Event:
        manualEntryItem = {
          type: PropertyType.String,
          propertyDefaultType: PropertyType.String,
          value: input,
          isSelected: this.isItemSelected(input, this.state.selected),
          resourceType: resourceTypeFilter,
          label,
          itemFromInput: true,
        };
        break;
      // adds a raw user property
      case ResourceType.User:
        manualEntryItem = {
          type: peoplePropertyDefinitionsStore?.value[input]?.type || PropertyType.String,
          propertyDefaultType: peoplePropertyDefinitionsStore?.value[input]?.type || PropertyType.String,
          value: input,
          isSelected: this.isItemSelected(input, this.state.selected),
          resourceType: resourceTypeFilter,
          label,
          itemFromInput: true,
        };
        break;
      // when "All" is selected, defaults to adding an event property
      default: {
        const resourceType = this.shouldShowEventProps()
          ? ResourceType.Event
          : this.canAccessUserProperties() && ResourceType.User;
        if (resourceType) {
          manualEntryItem = {
            resourceType,
            type: PropertyType.String,
            propertyDefaultType: PropertyType.String,
            value: input,
            label,
            itemFromInput: true,
          };
        }
        break;
      }
    }
    return manualEntryItem;
  }

  private canDisplayRecents() {
    return !(this.getSearchFilter() || this.props.hideRecentlyViewed);
  }

  private getRecentlyViewedSection(
    resourceTypes: ResourceType[],
    sections: Array<PropertySection | EventSection>,
  ): PropertySection | null {
    // hide recently viewed section when searching
    if (!this.canDisplayRecents()) {
      return null;
    }

    const allSectionsItems = sections.reduce(
      (a, b) => [...a, ...(b.items || [])],
      [] as (typeof sections)[number][`items`],
    );
    const allRecentItems = resourceTypes.flatMap((resourceType) => {
      return this.getRecentItems(resourceType).map((persistedItem) => {
        return {...persistedItem, resourceType};
      });
    });
    const sortedRecentItems = orderBy(
      allRecentItems,
      (item) => {
        return typeof item[RECENT_ITEM_TIMESTAMP_KEY] === `number`
          ? item[RECENT_ITEM_TIMESTAMP_KEY]
          : // old format for recent items didn't have timestamp field
            0;
      },
      `desc`,
    ).map((persistedItem) => omit(persistedItem, RECENT_ITEM_TIMESTAMP_KEY) as PropertySectionItem);

    return {
      label: this.t(`query-builder.properties-menu.recent-props`),
      items: sortedRecentItems
        .map((persistedItem): PropertySectionItem => {
          const persistedItemUniqueKey = getItemUniqueKey(persistedItem);
          const propertyItem = allSectionsItems.find((item) => {
            return getItemUniqueKey(item) === persistedItemUniqueKey;
          });
          if (!propertyItem) {
            return null;
          }
          return omit(propertyItem, `isSelected`) as PropertySectionItem;
        })
        .filter(Boolean)
        .slice(0, NUM_RECENT_ITEMS),
    };
  }

  private getMenuSections(): CombinedSection[] {
    if (this.hasStagedItem()) {
      return this.getSectionsForStagedItem(this.getStagedItemType(), this.peekStagedItem());
    } else if (this.props.listProperty) {
      return this.getObjectMenuSection();
    }
    return this.getTabbedMenuSections();
  }
  /**
   * Returns all sections to be rendered in the selection menu
   */
  private getTabbedMenuSections(): CombinedSection[] {
    let sections = this.getAllPropertiesSections();

    if (this.props.getSortedMenuSections) {
      sections = this.props.getSortedMenuSections(sections);
    }

    const eventNameKey = stableStringify(
      isQueryOnAllEvents(this.props.eventNames) ? [SPECIAL_EVENTS.ALL_EVENTS] : this.props.eventNames,
    );
    if (!this._instanceEventLoadHistory.has(eventNameKey) && !this.state.isLoading) {
      this._instanceEventLoadHistory.add(eventNameKey);
    }

    return sections;
  }

  /**
   * Returns a new sorted array.
   */
  private sortPropertiesByScore(
    properties: Array<PropertySectionItem | EventSectionItem>,
  ): Array<PropertySectionItem | EventSectionItem> {
    const searchScores = {};
    let maxSearchScore = 0;
    let maxPopularity = 0;
    properties.forEach((item) => {
      // todo: support fuzzy match with localized property names
      const searchScore =
        (this.state.searchFilter &&
          typeof item.label === `string` &&
          fuzzyMatch(item.label, this.state.searchFilter)?.score) ||
        0;
      searchScores[getItemUniqueKey(item)] = searchScore;
      if (searchScore > maxSearchScore) {
        maxSearchScore = searchScore;
      }

      const popularity = Number(item.uiUniqueQueryCount) || 0;
      if (popularity > maxPopularity) {
        maxPopularity = popularity;
      }
    });

    const SEARCH_SCORE_WEIGHT = 3;
    const RECENTLY_USED_WEIGHT = this.props.hideRecentlyViewed ? 0 : 2;
    const SELECTED_WEIGHT = this.props.multiple ? 2.5 : 0;
    const EVENT_WEIGHT = -0.01; // all things being equal, rank events below properties
    const DISABLED_PROPERTY_WEIGHT = 0.02; // all things being equal, rank disabled properties above enabled properties

    const propertiesWithMeta = properties.map((item) => {
      const isRecentlyUsed = isEventSectionItem(item) ? item.value.isRecentlyUsed : item.isRecentlyUsed;
      const isEventProperty = isPropertySectionItem(item) && item.resourceType === ResourceType.Event;
      let recentlyUsed = isRecentlyUsed ? 1 : 0;
      const globalRelevance = (isEventProperty && isPropertySectionItem(item) && item.densityGlobal) || 0;
      let localRelevance = (isEventProperty && isPropertySectionItem(item) && item.densityLocal) || 0;
      if (isPropertySectionItem(item) && isProperty(item) && isInlineCustomProperty(item.customPropertyId)) {
        recentlyUsed = 1;
        localRelevance = 1;
      }
      const isSelected = this.isItemSelected(item.value, this.state.selected) ? 1 : 0;
      const popularity = maxPopularity ? (Number(item.uiUniqueQueryCount) || 0) / maxPopularity : 0;
      const eventModifier = isEventSectionItem(item) ? EVENT_WEIGHT : 0;
      const searchScore = searchScores[getItemUniqueKey(item)];
      const normalizedSearchScore = maxSearchScore ? searchScore / maxSearchScore : 0;
      const disabledModifier =
        isPropertySectionItem(item) && isProperty(item) && this.isPropertyDisabled(item, item.value)
          ? DISABLED_PROPERTY_WEIGHT
          : 0;
      const score =
        normalizedSearchScore * SEARCH_SCORE_WEIGHT +
        recentlyUsed * RECENTLY_USED_WEIGHT +
        isSelected * SELECTED_WEIGHT +
        globalRelevance +
        localRelevance +
        popularity +
        eventModifier +
        disabledModifier;

      if (isNaN(score)) {
        throw Error(`Score is NaN.`);
      }

      return {
        item,
        score,
        scoreParts: [
          normalizedSearchScore * SEARCH_SCORE_WEIGHT,
          recentlyUsed * RECENTLY_USED_WEIGHT,
          globalRelevance,
          localRelevance,
          popularity,
          eventModifier,
        ],
      };
    });

    propertiesWithMeta.sort((a, b) => {
      return b.score - a.score;
    });

    if (this.props.currentProject.get().value?.hasFeatureFlag(ProjectFeatureFlag.Debug)) {
      propertiesWithMeta.forEach((entry) => {
        const meta = {...entry};
        delete meta.item;
        entry.item.subLabel = JSON.stringify(meta);
      });
    }

    return propertiesWithMeta.map((entry) => entry.item);
  }

  getStagedEventsMenuSections(stagedItem: PropertySectionItem): EventSection[] {
    const sections = this.getEventsSections({
      selected:
        stagedItem.menuItemType === MenuItemType.BorrowProperty
          ? this.state.selected?.borrowEvent
          : this.state.selected?.behavior?.event,
      mainSectionLabel: stagedItem.menuItemType === MenuItemType.BorrowProperty ? `Borrow from Event` : `Did Event`,
      type:
        stagedItem.menuItemType === MenuItemType.BorrowProperty
          ? MenuItemType.EventWithProperties
          : MenuItemType.Events,
      hasStagedItem: true,
    });
    return sections.map((section) => {
      return {
        ...section,
        items: this.sortProperties(section.items as PropertySectionItem[]) as EventSectionItem[],
      };
    });
  }

  private getEventsSections({
    type = MenuItemType.Events,
    selected,
    mainSectionLabel,
    hasStagedItem,
    excludeVisibleSection,
  }: {
    type?: MenuItemType.Events | MenuItemType.EventWithProperties;
    selected?: PropertySectionItem;
    mainSectionLabel?: string;
    hasStagedItem?: boolean;
    excludeVisibleSection?: boolean;
  } = {}): EventSection[] {
    const {print} = this.props.i18n;

    const sectionConfig: EventsControllerSectionConfig = {
      dataGroupIds: this.props.dataGroupIds,
      selected: {
        ...selected,
        label: print(selected?.label),
        subLabel: print(selected?.subLabel),
      },
      showAllEvents: this.props.showAllEvents,
      showAnyEvent: this.props.showAnyEvent,
      showTopEvents: this.props.showTopEvents,
      showSessionEvent: this.props.showSessionEvent,
      excludeCustomEvents: false,
      mainSectionShowLimit: MENU_ITEM_LIMIT,
      sortByValue: this.canSortItems() ? SortBy.Relevance : null,
      recentlyUsedEvents: this.getRecentItems(ResourceType.Event).map((item) => omit(item, RECENT_ITEM_TIMESTAMP_KEY)),
      subLabel: this.state.selectedFilter?.value === `all` && !hasStagedItem ? `Event${PATH_SEPARATOR}` : null,
      ...(mainSectionLabel && {mainSectionLabel}),
    };
    const eventsSections = excludeVisibleSection
      ? [this.props.eventsController.getHiddenSection(sectionConfig)]
      : (this.props.eventsController.getSections(sectionConfig) as Array<Section<EventItem>>);

    return [
      ...eventsSections.map((section) => ({
        ...section,
        type,
        items: section.items.map((item) => {
          return {
            ...item,
            menuItemType: type,
            hasCaret: type === MenuItemType.EventWithProperties,
          };
        }),
      })),
    ];
  }

  private getAllEventProperties(eventNames: string[]) {
    let eventProperties = [];
    if (this.shouldShowEventProps() && !this.state.loadingEventProperties) {
      const includeDimensionProperties = !this.props.excludeDimensionProperties;
      if (isQueryOnAllEvents(eventNames)) {
        eventProperties = getAllGlobalPropertyDefinitions();
      } else if (this.shouldFilterToEventPropertyIntersection()) {
        const eventPropertiesResults = eventNames.map((eventName) =>
          getAllPropertyDefinitionsForEvents([eventName], {includeDimensionProperties}),
        );
        // @ts-ignore
        eventProperties = calculatePropertiesIntersection(eventPropertiesResults);
      } else {
        eventProperties = getAllPropertyDefinitionsForEvents(eventNames, {includeDimensionProperties});
      }
    } else {
      return [];
    }

    eventProperties = eventProperties.map((prop) => getDataDefinitionsPropertyMenuItem(prop, this.props.i18n));

    eventProperties = this.addTempMappingsToProperties(ResourceType.Event, false, eventProperties);

    return eventProperties;
  }

  private handleSearchInput(searchFilter) {
    const isStartingSearch = Boolean(searchFilter && !this.state.searchFilter);
    const isClearingSearch = Boolean(this.state.searchFilter && !searchFilter);
    this.setState({searchFilter});
    if (isStartingSearch) {
      this.trackEvent(TRACKING_EVENTS.SEARCH_STARTED);
    } else if (isClearingSearch) {
      this.trackEvent(TRACKING_EVENTS.SEARCH_CLEARED);
    }
  }

  private debouncedSearchInput = debounce((searchInput) => this.handleSearchInput(searchInput), 200);

  isBorrowEventMenu(): boolean {
    return (
      this.state.stagedItemsStack[0]?.menuItemType === MenuItemType.BorrowProperty &&
      this.state.stagedItemType === StagedItemType.BorrowEvent
    );
  }

  isBorrowPropertyMenu(): boolean {
    return (
      this.state.stagedItemsStack[0]?.menuItemType === MenuItemType.BorrowProperty &&
      this.state.stagedItemType === StagedItemType.PropertiesForEvent
    );
  }

  trackBorrowPropertySelection({menuItemType}: {menuItemType: MenuItemType}): void {
    if (menuItemType === MenuItemType.BorrowProperty) {
      this.trackEvent(TRACKING_EVENTS.BORROW_PROPERTY_CREATE);
    } else if (this.isBorrowEventMenu()) {
      this.trackEvent(TRACKING_EVENTS.BORROW_PROPERTY_EVENT);
    } else if (this.isBorrowPropertyMenu()) {
      this.trackEvent(TRACKING_EVENTS.BORROW_PROPERTY_PROPERTY);
    }
  }

  private getEventProperties({
    includeHidden = false,
    onlyCustom = false,
    eventNames = null,
    excludedPropertyTypes = null,
  }: {
    includeHidden?: boolean;
    onlyCustom?: boolean;
    eventNames?: string[];
    excludedPropertyTypes?: PropertyType[];
  } = {}) {
    if (!this._cachedEventProperties) {
      this._cachedEventProperties = this.getAllEventProperties(eventNames ?? this.eventNames());
    }

    // we filter out custom properties here to prevent double count. custom properties from data definitions store & custom properties store are stitched together in getPropertyDefinitions
    // eventually, we want to abstract the stitching to the definitions store
    let eventProperties = this._cachedEventProperties.filter((prop) =>
      onlyCustom ? prop.status === `custom` : prop.status !== `custom`,
    );

    if (!includeHidden) {
      eventProperties = eventProperties.filter((prop) => !prop.hidden);
    }

    if (excludedPropertyTypes) {
      eventProperties = eventProperties.filter((prop) => {
        return !excludedPropertyTypes.includes(prop.type);
      });
    }

    return eventProperties;
  }

  private hasLookupTables() {
    return Object.keys(lookupTablesStore.value).length !== 0;
  }

  private expandLookupTableProperties(joinProperty: PropertySectionItem): PropertySectionItem[] {
    assert(isProperty(joinProperty));
    let lookupTableProperties: PropertySectionItem[] = [];
    const dataGroupId = joinProperty.tempDataGroupId || joinProperty.dimensionDataGroupId;
    const lookupProps: LookupTableProperty[] = lookupTablesStore.value[dataGroupId]?.properties;
    if (lookupProps) {
      const propertyDefinitionsForEventsStoreKey = this.getPropertyDefinitionsForEventsStoreKey();
      lookupTableProperties = lookupProps.map((lookupTableProp) => {
        return this.lookupTablePropToPropertySectionItem(
          joinProperty,
          lookupTableProp,
          propertyDefinitionsForEventsStoreKey,
        );
      });
    }
    return lookupTableProperties;
  }

  private lookupTablePropToPropertySectionItem(
    joinProperty: SectionItemProperty,
    lookupProperty: LookupTableProperty,
    propertyDefinitionsForEventsStoreKey: string,
  ) {
    const {
      resourceType,
      dimensionDataGroupId,
      tempDataGroupId,
      joinPropertyType,
      name: joinPropertyName,
      label: joinPropertyLabel,
      customPropertyId,
    } = joinProperty;
    const dataGroupId = tempDataGroupId || dimensionDataGroupId;
    return this.getPropertyMenuItem({
      property: {
        name: lookupProperty.name,
        label: lookupProperty.label,
        type: lookupProperty.type,
        propertyDefaultType: lookupProperty.type,
        count: lookupProperty.count,
        resourceType,
        dataGroupId,
        joinPropertyType,
        joinPropertyLabel: this.props.i18n.print(joinPropertyLabel),
        value: [joinPropertyName, lookupProperty.name],
        isLookupTableProperty: true,
        ...(tempDataGroupId ? {tempDataGroupId} : {}),
        ...(customPropertyId ? {customPropertyId} : {}),
      },
      propertyDefinitionsForEventsStoreKey,
      isDisplayingMultipleResources: this.isDisplayingMultipleResources(),
    });
  }

  /**
   * Expands a list of properties to join all mapped properties in a lookup table.
   * This expansion is only done when there is a search term in the dropdown
   */
  private getAllLookupTablePropertiesForDimensionProps(properties: TODO): TODO {
    if (!this.hasLookupTables() || this.props.excludeLookupTableProperties) {
      return [];
    }

    return properties
      .filter((property) => isProperty(property) && property.type === PropertyType.Dimension)
      .flatMap((property) => this.expandLookupTableProperties(property));
  }

  private getLookupTablePropertyFromSelected(selected: Item) {
    let properties = [];
    if (selected.resourceType === ResourceType.Event) {
      properties = this.getEventProperties();
    } else if (selected.resourceType === ResourceType.User) {
      properties = this.getUserProperties();
    }
    return properties.find((prop) => {
      return prop.name === selected.value[0];
    });
  }

  /**
   * Generates a single list of all available properties to select in the dropdown
   */
  private getAllPropertiesSections(): CombinedSection[] {
    const projectHasEvents = this.props.menuItemTypes?.includes(MenuItemType.Events);
    const projectHasEventProps = this.shouldShowEventProps();
    const projectHasDataGroups = this.props.allDataGroups;
    const otherMenuSection = this.getOtherMenuSection();

    // these are functions so we don't waste cycles when they aren't ultimately needed
    // based on the switch below
    const eventsSections = () => (projectHasEvents ? this.getEventsSections() : []);
    const hiddenEventSet = new Set(
      projectHasEvents
        ? this.getEventsSections({excludeVisibleSection: true})[0].items.map((item) => item.value.value)
        : [],
    );
    const eventPropSections = () => (projectHasEventProps ? this.getEventPropertiesMenuSections() : []);
    const userPropSections = () => (this.shouldShowUserProps() ? this.getUserPropertiesMenuSections() : []);
    const experimentSections = () => (this.shouldShowExperiments() ? this.getExperimentsMenuSections() : []);
    const dataGroupPropSections = () => (projectHasDataGroups ? this.getDataGroupsSections() : []);
    const cohortsSections = () => (this.shouldShowCohorts() ? this.getCohortsMenuSections() : []);
    const computedSections = () => (otherMenuSection.items.length > 0 ? [otherMenuSection] : []);

    const filter = this.state.selectedFilter;
    let sectionsOfAvailableProperties: Array<PropertySection | EventSection> = [];
    let allSectionLabel = ``;
    let recentsSection: CombinedSection | null = null;

    switch (filter.resourceType) {
      /** event properties */
      case ResourceType.Event:
        sectionsOfAvailableProperties = eventPropSections();
        recentsSection = this.getRecentlyViewedSection([ResourceType.Event], sectionsOfAvailableProperties);
        allSectionLabel = this.t(`query-builder.properties-menu.all-event-props`);
        break;
      /** profile properties */
      case ResourceType.User: {
        const dataGroupId = filter.dataGroupId;
        if (dataGroupId) {
          const dataGroup = this.state.dataGroups[dataGroupId];
          allSectionLabel = this.t(`query-builder.properties-menu.all-datagroup-props`, {
            dataGroupName: dataGroup.displayName,
          });
          sectionsOfAvailableProperties = [
            dataGroupPropSections().find((section) =>
              section.items.some((item) => `dataGroupId` in item && item.dataGroupId === dataGroupId),
            ),
          ].filter(Boolean);
        } else {
          sectionsOfAvailableProperties = userPropSections();
          allSectionLabel = this.t(`query-builder.properties-menu.all-user-props`);
        }
        recentsSection = this.getRecentlyViewedSection([ResourceType.User], sectionsOfAvailableProperties);
        break;
      }
      /** list of cohorts */
      case ResourceType.Cohort: {
        allSectionLabel = this.t(`query-builder.properties-menu.all-cohorts`);
        const [yourCohortsSection, otherCohortsSection] = cohortsSections();
        const [yourSelectedCohorts, yourUnselectedCohorts] = partition(
          yourCohortsSection.items,
          (item) => item.isSelected,
        );
        const [otherSelectedCohorts, otherUnselectedCohorts] = partition(
          otherCohortsSection.items,
          (item) => item.isSelected,
        );
        sectionsOfAvailableProperties = [
          {
            items: [
              ...yourSelectedCohorts,
              ...otherSelectedCohorts,
              ...yourUnselectedCohorts,
              ...otherUnselectedCohorts,
            ],
          },
        ];
        break;
      }
      case `experiments`:
        sectionsOfAvailableProperties = experimentSections();
        break;
      /** virtual props */
      case `computed`:
        sectionsOfAvailableProperties = [...computedSections()];
        break;

      /** list of event names and custom events */
      case `events`:
        allSectionLabel = this.t(`query-builder.properties-menu.all-events`);
        sectionsOfAvailableProperties = eventsSections();
        /**
         * @todo qb-menus -- it's not possible to fetch recent events from the
         * events menu since they have a different persistence key
         */
        break;
      /** all properties & events */
      default:
        allSectionLabel = this.t(`query-builder.properties-menu.all-props`);
        sectionsOfAvailableProperties = [
          ...computedSections(),
          ...eventsSections(),
          ...eventPropSections(),
          ...userPropSections(),
          ...dataGroupPropSections(),
          ...cohortsSections(),
          ...experimentSections(),
        ];

        /**
         * @todo qb-menus -- the recents section is not ordering by selection recency across
         * all different types of properties, it is selecting 2 from each type and displaying those
         * we don't persist data to order the resulting list of properties by selection time
         */
        recentsSection = this.getRecentlyViewedSection(
          [ResourceType.Event, ResourceType.User, ResourceType.Other],
          sectionsOfAvailableProperties,
        );
        break;
    }

    const allItems = sectionsOfAvailableProperties.flatMap((s) => {
      let items: Array<PropertySectionItem | EventSectionItem> = s?.items ?? [];

      /**
       * hides properties that belong to lookup tables in the main list of properties
       * unless the user is actively searching for something, this is done to
       * reduce clutter in what is initially presented to the user
       */
      if (!this.state.searchFilter) {
        items = items.filter((item) => (`isLookupTableProperty` in item ? !item.isLookupTableProperty : true));
      }

      return items;
    });

    // cohorts are already sorted in the order we want from the switch statement above
    let sorted = allItems;
    if (filter.resourceType !== ResourceType.Cohort) {
      sorted = this.sortProperties(sorted);
    }
    const filtered = sorted.filter((item) =>
      isPropertySectionItem(item) ? !item.hidden : !hiddenEventSet.has(item.value.value),
    );

    const sections: CombinedSection[] = [
      // adds the "Recently Viewed section based on the selected filter"
      recentsSection,
      {
        label: this.getFilterOptionSectionItemsForSidenav().length > 2 ? allSectionLabel : ``,
        items: filtered,
        showLimit: null,
        hideTopDivider: false,
      },
    ].filter(Boolean);

    const hiddenItems = sorted.filter((item) =>
      isPropertySectionItem(item) ? item.hidden : hiddenEventSet.has(item.value.value),
    );
    if (!this.state.isLoading) {
      sections.push({
        label: this.t(`query-builder.properties-menu.hidden-props`),
        items: hiddenItems,
        expansionToggle: {
          initialIsExpanded: false,
          labelMode: ExpansionToggleLabelMode.ShowHide,
        },
      });
    }

    return sections;
  }

  private getEventPropertiesMenuSections({
    item,
    hideSectionLabel,
    isForMultiAttribution,
  }: {
    item?: PropertySectionItem;
    hideSectionLabel?: boolean;
    isForMultiAttribution?: boolean;
  } = {}): PropertySection[] {
    const specialEventProperties: Array<SpecialControl | PropertySectionItem> = [];
    const eventNames = item?.value ? [item.value.value] : null;
    // Include all event properties for multi attribution property menu
    let properties: Array<PropertyDefinition | SpecialControl | PropertySectionItem> = this.getEventProperties({
      eventNames: isForMultiAttribution ? [SPECIAL_EVENTS.ANY_EVENT] : eventNames,
      excludedPropertyTypes: this.isBorrowPropertyMenu() ? EXCLUDED_PROPERTY_TYPES_FOR_BORROW_PROP : null,
    });

    if (this.shouldShowEventProps() && this.props.includeCustomProperties !== IncludeCustomPropertyType.None) {
      properties = [...properties, ...this.getCustomFormulas([ResourceType.Event])];
      if (!this.props.enableWorkspaceFilterRestrictions) {
        // workspace filters can use inline custom properties, but cannot refer to persisted custom properties
        properties = [...properties, ...this.getCustomProperties(ResourceType.Event)];
      }
    }

    if (!isForMultiAttribution && this.props.showSpecialDatasetProperty) {
      specialEventProperties.push(DATASET_PROPERTY);
    }

    const specialPropertyMenuItems = this.getPropertyMenuItems({properties: specialEventProperties});
    let propertyMenuItems: Array<PropertySectionItem | EventSectionItem> = this.getPropertyMenuItems({
      properties,
      resourceType: ResourceType.Event,
    });

    /*
      Removing this sort causes the following test to fail
      [Widgets:mp-query-builder:lookup tables:mapped custom properties:table properties are selectable via search]
      TODO: Research if this is an incorrect test check or if something inside propertyMenuItems is truly required
    */
    propertyMenuItems = this.sortProperties(propertyMenuItems);
    propertyMenuItems = this.prepareDimensionProperties(propertyMenuItems);

    if (this.useMultipleSelection()) {
      // the multi-select mode is mostly used by the users report and live view column pickers, where
      // we hard-code some default properties to begin with. But they might not be part of the
      // project's property set. We include those that are not so they still show up in the
      // picker and can be toggled off
      const selected = this.state.selected;

      const isDisplayingMultipleResources = this.isDisplayingMultipleResources();
      if (Array.isArray(selected)) {
        const propertyDefinitionsForEventsStoreKey = this.getPropertyDefinitionsForEventsStoreKey();
        propertyMenuItems = [
          ...propertyMenuItems,
          ...selected
            .filter((selectedItem) => !properties.find((p) => `value` in p && p.value === selectedItem.value))
            .map((prop) =>
              this.getPropertyMenuItem({
                isDisplayingMultipleResources,
                property: prop,
                selected: prop,
                propertyDefinitionsForEventsStoreKey,
              }),
            ),
        ];
      }
    }

    let eventPropertyItems = [
      // special properties should always be on top
      ...specialPropertyMenuItems,
      ...(this.sortProperties(propertyMenuItems) as PropertySectionItem[]),
    ];

    if (isForMultiAttribution) {
      eventPropertyItems = this.filterMultiAttributionProperties(eventPropertyItems);
    }
    const eventPropertiesSection = {
      type: MenuItemType.EventProperties,
      isLoading: this.state.loadingEventProperties,
      items: eventPropertyItems,
      label: hideSectionLabel ? null : this.t(`query-builder.properties-menu.event-props`),
      showLimit: 0,
    };

    return [eventPropertiesSection, ...this.getHiddenEventPropertiesMenuSections({isForMultiAttribution})];
  }

  private getHiddenEventPropertiesMenuSections({
    isForMultiAttribution,
  }: {isForMultiAttribution?: boolean} = {}): PropertySection[] {
    const hiddenProperties = this.getEventProperties({
      includeHidden: true,
      excludedPropertyTypes: this.isBorrowPropertyMenu() ? EXCLUDED_PROPERTY_TYPES_FOR_BORROW_PROP : null,
    }).filter((def) => def.hidden);
    let eventPropertyItems = this.getPropertyMenuItems({
      properties: hiddenProperties,
      resourceType: ResourceType.Event,
    });
    if (isForMultiAttribution) {
      eventPropertyItems = this.filterMultiAttributionProperties(eventPropertyItems);
    }
    return [
      {
        type: MenuItemType.EventProperties,
        isLoading: this.state.loadingEventProperties,
        items: eventPropertyItems,
        label: this.t(`query-builder.properties-menu.hidden-event-props`),
        showLimit: MENU_ITEM_LIMIT,
        isHidden: true,
      },
    ];
  }

  private isVisibleCustomPropertyForEvents(customProperty: CustomProperty): boolean {
    if (isBorrowedPropertyReferenced(customProperty)) {
      return this.showBorrowedProperties();
    }

    const eventNames = this.eventNames();

    if (isQueryOnAllEvents(eventNames)) {
      return true;
    }

    const eventPropertiesNameSet = new Set<string>(
      this.getEventProperties({includeHidden: true}).map((prop) => prop.name),
    );

    // $date is a special event property which exists on all events, but is not always returned
    // by getEventProperties
    eventPropertiesNameSet.add(MPProperties.Date);

    const isValidIfBorrowProperty = (): boolean => {
      return (
        !this.isBorrowPropertyMenu() || !EXCLUDED_PROPERTY_TYPES_FOR_BORROW_PROP.includes(customProperty.propertyType)
      );
    };

    if (isPersistedCustomProperty(customProperty.customPropertyId)) {
      const referencedEventProperties = customProperty.referencedRawEventProperties || [];
      // persisted custom properties know from the backend response all the transitively
      // referenced raw event properties
      const hasValidReferencesProperties =
        referencedEventProperties.length === 0 ||
        Object.values(customProperty.referencedRawEventProperties).some((property: string) =>
          eventPropertiesNameSet.has(property),
        );
      return hasValidReferencesProperties && isValidIfBorrowProperty();
    }
    const hasUserPropertiesOnly = Object.values(customProperty.composedProperties).every(
      (prop: PropertySectionItem) => prop.resourceType === ResourceType.User,
    );

    // for non-persisted custom properties we need to compute the transitively referenced
    // raw event properties by combining the referenced properties of any nested custom events
    // and any properties referenced directly by this property
    const hasValidComposedProperties =
      hasUserPropertiesOnly ||
      Object.values(customProperty.composedProperties).some((composedProperty: any) => {
        if (composedProperty.customPropertyId) {
          const referencedEventProperties =
            customPropertiesStore.value[composedProperty.customPropertyId].referencedRawEventProperties;
          return (
            referencedEventProperties.length === 0 ||
            referencedEventProperties.some((property: string) => eventPropertiesNameSet.has(property))
          );
        }
        return (
          composedProperty.resourceType === ResourceType.Event && eventPropertiesNameSet.has(composedProperty.value)
        );
      });
    return hasValidComposedProperties && isValidIfBorrowProperty();
  }

  private isValidGroupModeCustomProperty(customPropertyId: string): boolean {
    const hasMultipleNonUserDataGroups = this.getNonUserdataGroupIds().length > 1;
    const effectiveDataGroupId = getSingleDataGroupId(this.props.dataGroupIds);

    if (!effectiveDataGroupId && !hasMultipleNonUserDataGroups) {
      // no data groups selected. all custom properties are valid in user mode
      return true;
    }

    const id = isPersistedCustomProperty(customPropertyId) ? parseInt(customPropertyId) : customPropertyId;
    const customProperty = customPropertiesStore.value[id];

    const containsInvalidComposedProperty = Object.values(customProperty?.composedProperties || {}).some(
      (composedProperty) => {
        const containsUser = composedProperty.resourceType === ResourceType.User && !composedProperty.dataGroupId;
        const containsInvalidDataGroup =
          Boolean(composedProperty.dataGroupId) &&
          (composedProperty.dataGroupId !== effectiveDataGroupId || hasMultipleNonUserDataGroups);
        return containsUser || containsInvalidDataGroup;
      },
    );

    if (containsInvalidComposedProperty) {
      return false;
    }

    const nestedCustomComposedProperties = Object.values(customProperty?.composedProperties || {}).filter(
      (composedProperty) => Boolean(composedProperty.customPropertyId),
    );

    const containsInvalidNestedCustomComposedProperty =
      nestedCustomComposedProperties.length > 0 &&
      !nestedCustomComposedProperties.every((composedProperty) =>
        this.isValidGroupModeCustomProperty(composedProperty.customPropertyId),
      );

    return !containsInvalidNestedCustomComposedProperty;
  }

  private isVisibleCustomProperty(customPropertyId: string, dataGroupId: string): boolean {
    if (!isPersistedCustomProperty(customPropertyId)) {
      return true;
    }

    const id = isPersistedCustomProperty(customPropertyId) ? parseInt(customPropertyId) : customPropertyId;
    const customProperty = customPropertiesStore.value[id];
    const property = storeCustomPropertyToProperty(customPropertyId);

    // exclude persisted behavioral props
    if (
      this.props.includeCustomProperties === IncludeCustomPropertyType.PersistedNonBehavioralOnly &&
      isBehavioralCustomProperty(customProperty)
    ) {
      return false;
    }

    if (property.resourceType === ResourceType.Event && !this.isVisibleCustomPropertyForEvents(customProperty)) {
      return false;
    }

    // for custom profile properties, exclude those created from other data groups
    if (property.resourceType === ResourceType.User && (property.dataGroupId ?? null) !== (dataGroupId ?? null)) {
      return false;
    }

    return customProperty.isVisible || customProperty.user === this.getUserId();
  }

  private getCustomEntities(resourceTypes: ResourceType[], dataGroupId?: string) {
    // TODO @jimmy: add uiUniqueQueryCount as a field in the custom properties API
    const customDefinitions = keyBy(
      this.getEventProperties({includeHidden: false, onlyCustom: true}),
      `customPropertyId`,
    );

    const propertyDefinitionsForEventsStoreKey = this.getPropertyDefinitionsForEventsStoreKey();

    const isDisplayingMultipleResources = this.isDisplayingMultipleResources();
    return Object.keys(customPropertiesStore.value)
      .filter((customPropertyId) => !customPropertiesStore.value[customPropertyId].newCustomPropertyId)
      .filter(
        (customPropertyId) =>
          this.props.includeCustomProperties === IncludeCustomPropertyType.All ||
          isPersistedCustomProperty(customPropertyId),
      )
      .filter((customPropertyId) => this.isVisibleCustomProperty(customPropertyId, dataGroupId))
      .filter((customPropertyId) => this.isValidGroupModeCustomProperty(customPropertyId))
      .map((customPropertyId) => {
        let property = storeCustomPropertyToProperty(customPropertyId);
        const propertyDef = customDefinitions[customPropertyId];

        property = {
          ...property,
          ...(propertyDef ? {uiUniqueQueryCount: propertyDef.uiUniqueQueryCount} : {}),
        };

        return this.getPropertyMenuItem({
          isDisplayingMultipleResources,
          property,
          resourceType: property.resourceType,
          propertyDefinitionsForEventsStoreKey,
        });
      })
      .filter((item) => resourceTypes.includes(item.resourceType));
  }

  private getCustomProperties(resourceType: ResourceType, dataGroupId?: string) {
    let properties = this.getCustomEntities([resourceType], dataGroupId).filter((item) =>
      isPersistedCustomProperty(isProperty(item) && item.customPropertyId),
    );
    if (resourceType === ResourceType.Event || resourceType === ResourceType.User) {
      properties = this.addTempMappingsToProperties(resourceType, true, properties);
    }
    return properties;
  }

  private getCustomFormulas(resourceTypes: ResourceType[]) {
    return this.getCustomEntities(resourceTypes).filter(
      (item) => !isPersistedCustomProperty(isProperty(item) && item.customPropertyId),
    );
  }

  private getUserProperties({includeHidden = false}: {includeHidden?: boolean} = {}): PropertyDefinition[] {
    if (!this.shouldShowUserProps() || this.state.loadingPeopleProperties) {
      return [];
    }
    // we filter out custom properties here to prevent double count. custom properties from data definitions store & custom properties store are stitched together in getPropertyDefinitions
    // eventually, we want to abstract the stitching to the definitions store
    let userProperties = getAllPropertyDefinitionsForUserProfiles()
      .filter((prop) => prop.status !== `custom`)
      .map((prop) => getDataDefinitionsPropertyMenuItem(prop, this.props.i18n));

    if (!includeHidden) {
      userProperties = userProperties.filter((prop) => !prop.hidden);
    }

    userProperties = this.addTempMappingsToProperties(ResourceType.User, false, userProperties);

    return userProperties;
  }

  private isFirstEventTimeEnabled({forProfileSection}: {forProfileSection: boolean}): boolean {
    const hasComputedSection = this.props.otherOptions.length > 0;
    if (hasComputedSection && forProfileSection) {
      // Only ever show in the profileSection if there is no computed section, like in the cohorts builder or Users Report filter
      return false;
    }

    // first event time is backed by a custom behavioral property:
    // - behavioral properties are not allowed in workspace filters
    // - first event time is only valid in situations where custom properties are valid
    return (
      !this.props.enableWorkspaceFilterRestrictions &&
      ![IncludeCustomPropertyType.PersistedNonBehavioralOnly, IncludeCustomPropertyType.None].includes(
        this.props.includeCustomProperties,
      )
    );
  }

  private sortProperties(properties: Array<PropertySectionItem | EventSectionItem>) {
    if (this.canSortItems()) {
      return this.sortPropertiesByScore(properties);
    }
    return sortItemsByLabel(properties);
  }

  private isBehavioralPropertiesEnabled(): boolean {
    // behavioral properties can never be used in workspace filters
    if (this.props.enableWorkspaceFilterRestrictions) {
      return false;
    }
    // if the containing report allows for behavioral props they are always available
    if (this.props.allowBehavioralPropertyCreation) {
      return true;
    }
    // otherwise feature flag determines availability (feature flag is being phased out,
    // eventually only reports with allow-behavioral-property-creation will support them)
    return this.props.currentProject.get().value?.hasFeatureFlag(ProjectFeatureFlag.BehavioralProperties);
  }

  private prepareDimensionProperties(properties: TODO) {
    if (this.props.excludeDimensionProperties) {
      return this.unmapDimensionProperties(properties);
    }
    return [...properties, ...this.getAllLookupTablePropertiesForDimensionProps(properties)];
  }

  private unmapDimensionProperties(properties: TODO): TODO {
    // Make mapped properties look like they are unmapped.
    const [dimensionProperties, allOtherProperties] = partition(
      properties,
      (property) => isProperty(property) && property.type === PropertyType.Dimension,
    );
    const dimensionKeysToRemove = [
      `dimensionDataGroupId`,
      `icon`,
      `joinPropertyType`,
      `join_property_type`, // Todo: Find out how this key still exists, it should have been reformatted.
    ];
    const propertyDefinitionsForEventsStoreKey = this.getPropertyDefinitionsForEventsStoreKey();

    const isDisplayingMultipleResources = this.isDisplayingMultipleResources();
    return [
      ...allOtherProperties,
      ...dimensionProperties.map((property) =>
        this.getPropertyMenuItem({
          isDisplayingMultipleResources,
          property: {
            ...(omit(property, dimensionKeysToRemove) as PropertySectionItem),
            type: isProperty(property) && property.joinPropertyType,
          },
          propertyDefinitionsForEventsStoreKey,
        }),
      ),
    ];
  }

  private getExperimentsMenuSections(): PropertySection[] {
    const experiments = this.experimentNames;
    const experimentsSection: PropertySection = {
      type: MenuItemType.BorrowProperty,
      isLoading: this.state.loadingExperiments,
      items: experiments.map((experiment) =>
        this.getPropertyMenuItem({
          resourceType: ResourceType.Event,
          propertyDefaultType: PropertyType.String,
          property: {
            name: experiment,
            label: experiment,
            icon: `experiment`,
            value: `$experiment:${experiment}`,
            type: PropertyType.String,
            resourceType: ResourceType.Event,
            apiQueryCount: undefined,
            uiQueryCount: undefined,
            propertyDefaultType: PropertyType.String,
          },
          isDisplayingMultipleResources: false,
        }),
      ),
      label: this.t(`query-builder.properties-menu.experiments`),
    };
    return [experimentsSection];
  }

  private getUserPropertiesMenuSections(): PropertySection[] {
    let properties: Array<PropertyDefinition | PropertySectionItem> = this.getUserProperties();
    properties = [
      // peoplePropertiesStore properties
      ...properties,
      // special first event time property
      ...(this.isFirstEventTimeEnabled({forProfileSection: true}) ? [FIRST_EVENT_TIME_PROPERTY] : []),
    ];

    if (this.props.includeCustomProperties !== IncludeCustomPropertyType.None) {
      properties = [...properties, ...this.getCustomFormulas([ResourceType.User])];
      if (!this.props.enableWorkspaceFilterRestrictions) {
        // workspace filters can use inline custom properties, but cannot refer to persisted custom properties
        properties = [...properties, ...this.getCustomProperties(ResourceType.User)];
      }
    }

    let propertySectionItems = this.sortProperties(
      this.getPropertyMenuItems({properties, resourceType: ResourceType.User}),
    );

    propertySectionItems = this.prepareDimensionProperties(propertySectionItems);

    if (this.props.menuItemTypes?.includes(MenuItemType.SCDProperty)) {
      propertySectionItems = propertySectionItems.filter((item) => isScdPropertyName(item.name));
    }

    const userPropertiesSection = {
      type: MenuItemType.UserProperties,
      isLoading: this.state.loadingPeopleProperties,
      items: propertySectionItems as PropertySectionItem[],
      label: this.t(`query-builder.properties-menu.profile-props`),
      showLimit: MENU_ITEM_LIMIT,
    };

    return [userPropertiesSection, ...this.getHiddenUserMenuSections()];
  }

  private getHiddenUserMenuSections() {
    let hiddenProperties = this.getUserProperties({includeHidden: true}).filter((def) => def.hidden);
    /*
      TODO (duc): this is a quick patch (which hide the dim prop instead of showing it as a normal prop)
      Need to make user properties re-fecth correctly
    */
    if (this.props.excludeDimensionProperties) {
      hiddenProperties = hiddenProperties.filter((prop) => prop.type !== `dimension`);
    }
    return [
      {
        type: MenuItemType.UserProperties,
        isLoading: this.state.loadingPeopleProperties,
        items: this.getPropertyMenuItems({properties: hiddenProperties, resourceType: ResourceType.User}),
        label: this.t(`query-builder.properties-menu.hidden-profile-props`),
        showLimit: MENU_ITEM_LIMIT,
        isHidden: true,
      },
    ];
  }

  private isYourCohort(cohort) {
    return cohort.createdBy && cohort.createdBy.id === this.getUserId();
  }

  /** Get the non-user data group ids from props.dataGroupIds. Ignores props.dataGroupId */
  private getNonUserdataGroupIds(): string[] {
    return this.props.dataGroupIds ? Array.from(this.props.dataGroupIds).filter((id) => id !== null) : [];
  }

  private getCohortsMenuSections(): PropertySection[] {
    // ok to use getSingleDataGroupId() because we hide the cohorts menu section for > 1 data group (including user data group)
    const dataGroupId = getSingleDataGroupId(this.props.dataGroupIds);
    const cohorts = getSerializedCohorts(cohortsStore, dataGroupId);

    let inlineCohorts;
    let [userCohorts, allCohorts] = partition(cohorts, (cohort) => this.isYourCohort(cohort));
    // @ts-ignore - TODO: Type 'PropertyItem[]' is not assignable to type 'Cohort[]'.
    [inlineCohorts, userCohorts] = partition(
      this.getPropertyMenuItems({
        properties: userCohorts,
        resourceType: ResourceType.Cohort,
      }),
      (cohort) => cohort.value && cohort.value.cohort && cohort.value.cohort.raw_cohort,
    );

    allCohorts = allCohorts.filter((cohort) => cohort.canView);
    // @ts-ignore - TODO: Type 'PropertyItem[]' is not assignable to type 'Cohort[]'.
    allCohorts = this.getPropertyMenuItems({
      properties: allCohorts,
      resourceType: ResourceType.Cohort,
    });
    // @ts-ignore - TODO: CohortItem[] is not assignable to type 'PropertySectionItem[]'.
    allCohorts = this.sortPropertiesByScore(
      //@ts-ignore - TODO: CohortItem[] is not assignable to type 'PropertySectionItem[]'
      allCohorts.map((cohort) => ({
        ...cohort,
        // @ts-ignore - TODO: Type 'CohortItem is not assignable to type 'PropertySectionItem'.
        isRecentlyUsed: this.isPropertyRecentlyUsed(cohort),
      })),
    );

    if (this.props.specialCohorts?.length > 0) {
      // @ts-ignore - TODO: Type 'PropertyItem[]' is not assignable to type 'Cohort[]'.
      allCohorts.unshift(...this.props.specialCohorts);
    }

    const yourCohortsSection = {
      type: MenuItemType.Cohorts,
      isLoading: this.state.loadingCohorts,
      items: [...inlineCohorts, ...userCohorts],
      label: this.t(`query-builder.properties-menu.your-cohorts`),
    };

    const otherCohortsSection = {
      type: MenuItemType.Cohorts,
      isLoading: this.state.loadingCohorts,
      items: allCohorts,
      label: null,
      showLimit: userCohorts.length > 0 ? MENU_ITEM_LIMIT : null,
    };

    // @ts-ignore - TODO: Type 'CohortItem[]' is not assignable to type 'PropertyItem[]'.
    return [yourCohortsSection, otherCohortsSection];
  }

  private getOtherMenuSection(): PropertySection {
    const items = this.props.otherOptions
      .filter((item) => {
        return (
          OTHER_OPTIONS.has(item.value) &&
          (item.value !== FIRST_EVENT_TIME_PROPERTY.value || this.isFirstEventTimeEnabled({forProfileSection: false}))
        );
      })
      .map((item) => {
        // Behaviors are computed in a special way that creates a separate prop down the line. We want to normalize it first in that case
        const normalizedSelection = this.state.selected?.behavior
          ? getMenuItemForBehavior(this.state.selected.behavior)
          : this.state.selected;
        const isSelected = this.isItemSelected(item.value, normalizedSelection);
        const isDisabled = this.isPropertyDisabled(item, item.value);
        const dataGroupId = getSingleDataGroupId(this.props.dataGroupIds);
        const dataGroups = Object.values(this.state.dataGroups);
        const itemLabel = this.props.i18n.print(
          hasDynamicLabel(item)
            ? formatDataGroupLabel({label: item.dynamicLabel, dataGroupId, dataGroups})
            : item.label,
        );
        const mappedItem = {
          ...item,
          icon: `computed`,
          label: itemLabel,
          tooltipData: {
            ...item.tooltipData,
            header: itemLabel,
          },
          isSelected,
          isDisabled,
          ...(isDisabled ? {hasCaret: false, tooltip: {text: this.props.disabledItems[item.value]}} : {}),
        };
        return mappedItem;
      });
    return {
      isLoading: false,
      items,
      label: this.t(`query-builder.properties-menu.sidebar-computed`),
    };
  }

  /**
   * Generates items-menu sections from each loaded data group
   */
  private getDataGroupsSections(): PropertySection[] {
    // ok to use getSingleDataGroupId because getVisibleDataGroups() returns an empty array with >1 non-user data group
    const reportDataGroupId = getSingleDataGroupId(this.props.dataGroupIds);
    return this.getVisibleDataGroups().map((group) => {
      const displayDataGroupFirstEventTime =
        this.isFirstEventTimeEnabled({forProfileSection: true}) && dataGroupIDsEqual(reportDataGroupId, group.groupID);
      let properties = group.properties.filter((prop) => prop.status !== `custom`);

      // The latest value of SCD properties will begin to persist on group profiles as standard properties, sharing the same name as the SCD prop, without the prefix.
      // These properties are accessed, behind the scenes by a transformation from the SCD property itself, so don't need to be explicitly shown to the user.
      // These should only be displayed for use in user-queries, but group queries should always just use the standard SCD property.
      properties = this.getPropertiesExceptLatestHistorical(properties);

      const customGroupProps = this.getCustomProperties(ResourceType.User, group.groupID);
      properties = [...properties, ...customGroupProps];
      const sortedProperties = this.sortProperties([
        // all data group properties
        ...properties,
        /// special
        ...(displayDataGroupFirstEventTime ? [FIRST_EVENT_TIME_PROPERTY] : []),
      ]);

      return {
        isLoading: group.isLoading,
        items: this.getPropertyMenuItems({
          dataGroupId: group.groupID,
          properties: sortedProperties as PropertySectionItem[],
          resourceType: ResourceType.User,
        }),
        label: `${group.displayName} Properties`,
        showLimit: MENU_ITEM_LIMIT,
      };
    });
  }

  private getVisibleDataGroups(): DataGroupItem[] {
    const dataGroups = Object.values(this.state.dataGroups);

    const nonUserGroupIds = this.getNonUserdataGroupIds();

    if (nonUserGroupIds.length === 0) {
      // If only user data group (null) exists, show all
      return dataGroups;
    } else if (nonUserGroupIds.length === 1) {
      // If exactly one non-user data group, show only that group
      const groupId = nonUserGroupIds[0];
      const filtered = dataGroups.filter((group) => group.groupID === groupId);
      return filtered;
    }
    return [];
  }

  private getPropertiesExceptLatestHistorical(properties: PropertySectionItem[]): PropertySectionItem[] {
    const scdPropsWithoutPrefix = new Set<string>(
      properties.filter((prop) => isScdPropertyName(prop.name)).map((prop) => renameSCDProp(prop.name)),
    );

    return properties.filter((prop) => !scdPropsWithoutPrefix.has(prop.name));
  }

  private eventNames(): string[] {
    // if the staged item stack is comprised of an event then we are looking at a diff set of properties
    const item = this.state.stagedItemsStack.find(
      (stagedItem) => stagedItem.menuItemType === MenuItemType.EventWithProperties,
    );
    if (item) {
      return [item.value.value];
    }
    return this.props.eventNames && uniq<string>(this.props.eventNames).filter(Boolean);
  }

  eventPropertiesDefinitionsStoreCallback = () => {
    this.setState({loadingEventProperties: false});
  };

  globalEventPropertiesDefinitionsStoreCallback = () => {
    this.setState({loadingGlobalEventProperties: false});
    this.getTooltipData.cache.clear();
  };

  private fetchGlobalEventProperties({refresh = false}: {refresh?: boolean} = {}) {
    // Tooltip data currently depends on global event property definitions.
    // We need to ensure global properties are subscribed and updated.
    this.setState({loadingGlobalEventProperties: true}, async () => {
      propertyDefinitionsForEventsStore.unsubscribeAll(this.globalEventPropertiesDefinitionsStoreCallback);
      await fetchGlobalEventPropertyDefinitions({
        refresh,
        callback: this.globalEventPropertiesDefinitionsStoreCallback,
      });
    });
  }

  /**
   * Fetches event properties for a set of events or all events
   */
  private async fetchEventProperties({refresh = false}: {refresh?: boolean} = {}) {
    const eventNames = this.eventNames();
    if (this.shouldShowEventProps()) {
      const includeDimensionProperties = !this.props.excludeDimensionProperties;

      if (this.shouldFilterToEventPropertyIntersection()) {
        this.setState({loadingEventProperties: true});
        await Promise.all(
          eventNames.map((name) => fetchPropertyDefinitionsForEvents([name], {refresh, includeDimensionProperties})),
        );

        // We cannot use store subscription callbacks for multiple queries.
        // Check that events have not changed before clearing the loading state.
        if (isEqual(eventNames, this.eventNames())) {
          this.eventPropertiesDefinitionsStoreCallback();
        }
      } else if (!isQueryOnAllEvents(eventNames)) {
        this.setState({loadingEventProperties: true}, async () => {
          propertyDefinitionsForEventsStore.unsubscribeAll(this.eventPropertiesDefinitionsStoreCallback);
          const propertyCallsToMake = [
            fetchPropertyDefinitionsForEvents(eventNames, {
              refresh,
              includeDimensionProperties,
            }),
          ];
          await Promise.all(propertyCallsToMake);
          this.eventPropertiesDefinitionsStoreCallback();
        });
      }
    }
  }

  /**
   * Updates a given dataGroup with its list of properties from the store - otherwise fetches and then updates
   */
  private async fetchDataGroupResourceProperties(resourceType: ResourceType, dataGroupId: string) {
    let properties = [];
    const params = {
      dataGroupId,
      projectId: this.sourceAttrs.projectId,
      workspaceId: this.sourceAttrs.workspaceId,
    };

    try {
      if (resourceType === ResourceType.User) {
        // If store isn't populated by this point, await the fetch
        if (Object.keys(groupPropertyDefinitionsStore.value).length === 0) {
          await fetchAllPropertyDefinitionsForGroupProfiles(dataGroupStore.value);
        }
        properties = Object.values(groupPropertyDefinitionsStore.value).filter(
          (groupProperty) => groupProperty.dataGroupId === dataGroupId,
        );
      } else if (resourceType === ResourceType.Event) {
        properties = await this._eventPropertiesClient.query(params);
      } else {
        throw new Error(`Failed to fetch properties for invalid resource type ${resourceType}`);
      }
    } catch (error) {
      console.error(`Error loading data group (${dataGroupId}) user properties`, error);
    }

    this.setState((state) => ({
      dataGroups: {
        ...state.dataGroups,
        [dataGroupId]: {
          ...state.dataGroups[dataGroupId],
          isLoading: false,
          properties,
        },
      },
    }));
  }

  /**
   * Loads all data groups from the store and triggers their
   * respective properties request
   */
  private loadDataGroups() {
    if (!this.props.allDataGroups) {
      return this.setState({
        dataGroups: {},
        isLoadingDataGroups: false,
      });
    }
    const dataGroups: PropertiesMenuState[`dataGroups`] = {};

    const groups = Object.values(dataGroupStore.value);
    for (const group of groups) {
      const dataGroupId = group.groupID;
      const cachedProperties = this._peoplePropertiesClient.getCachedResponse({
        dataGroupId,
        projectId: this.sourceAttrs.projectId,
        workspaceId: this.sourceAttrs.workspaceId,
      });

      dataGroups[group.groupID] = {
        ...group,
        isLoading: cachedProperties ? false : true,
        properties: cachedProperties || [],
      };
    }

    // only fetch properties for data groups that are not cached
    this.setState({dataGroups, isLoadingDataGroups: false});
    groups.forEach(({groupID}) => {
      if (dataGroups[groupID].isLoading) {
        this.fetchDataGroupResourceProperties(ResourceType.User, groupID);
      }
    });
  }

  private async fetchLookupTables() {
    const shouldFetchLookupTables = !this.props.excludeLookupTableProperties && !this.props.excludeDimensionProperties;
    if (shouldFetchLookupTables) {
      lookupTablesStore.subscribe(this.lookupTablesStoreCallback);

      if (!lookupTablesStore.hasFetchedValue()) {
        await lookupTablesStore.fetch({
          ...(this.sourceAttrs.projectId ? {projectId: this.sourceAttrs.projectId} : {}),
          ...(this.sourceAttrs.workspaceId ? {workspaceId: this.sourceAttrs.workspaceId} : {}),
        });
      }
    }
  }

  private fetchUserDetails() {
    return userDetailsStore.fetch();
  }

  private async fetchCohorts() {
    if (!this.props.projectHasCohorts) {
      return;
    }

    this.setState({loadingCohorts: true});

    // ok to use getSingleDataGroupId() because cohorts section is hidden w/ > 1 data group
    const dataGroupId = getSingleDataGroupId(this.props.dataGroupIds);

    if (dataGroupId && this.props.supportB2bCohorts) {
      cohortsStore.subscribe(this.cohortStoreCallback, dataGroupId);
      await cohortsStore.fetch(
        {
          projectId: this.sourceAttrs.projectId,
          workspaceId: this.sourceAttrs.workspaceId,
          dataGroupId,
        },
        {},
        dataGroupId,
      );
    } else {
      cohortsStore.subscribe(this.cohortStoreCallback);
      await cohortsStore.fetch({
        projectId: this.sourceAttrs.projectId,
        workspaceId: this.sourceAttrs.workspaceId,
      });
    }
  }

  private async fetchExperiments() {
    this.setState({loadingExperiments: true});

    const params = {
      eventName: getExperimentKey(AutoDetectExperimentKeys.EXPERIMENT_EVENT_NAME, this.sourceAttrs.projectId),
      propertyName: getExperimentKey(AutoDetectExperimentKeys.EXPERIMENT_PROPERTY_NAME, this.sourceAttrs.projectId),
      projectId: this.sourceAttrs.projectId,
      workspaceId: this.sourceAttrs.workspaceId,
    };
    let names = eventPropertyValuesClient.getCachedResponse(params);
    if (names === null || names === undefined) {
      names = await eventPropertyValuesClient.query(params, {
        forceCache: true,
      });
    }
    this.experimentNames = names ?? [];

    this.setState({
      loadingExperiments: false,
    });
    this.getTooltipData.cache.clear();
  }

  private getPropertyMenuItems({
    properties,
    dataGroupId,
    resourceType,
    selected,
    propertyDefaultType,
  }: {
    properties?: Array<CohortItem | PropertySectionItem | PropertyDefinition | SpecialControl | Selection>;
    dataGroupId?: string;
    resourceType?: ResourceType;
    selected?: any;
    propertyDefaultType?: PropertyType;
  } = {}): PropertySectionItem[] {
    const propertyDefinitionsForEventsStoreKey = this.getPropertyDefinitionsForEventsStoreKey();

    let filterTypes = this.hasStagedItem() ? [] : this.props.typeFilter;

    // only show numeric properties for numeric computed options
    if (this.getStagedItemType() === StagedItemType.ComputedListNumericProperty) {
      filterTypes = [PropertyType.Number];
    }

    if (filterTypes.length) {
      properties = properties.filter((prop) => `type` in prop && filterTypes.includes(prop.type));
    }

    const isDisplayingMultipleResources = this.isDisplayingMultipleResources();

    let items = properties.map((property) => {
      const result = this.getPropertyMenuItem({
        isDisplayingMultipleResources,
        dataGroupId,
        property,
        resourceType,
        selected,
        propertyDefaultType,
        propertyDefinitionsForEventsStoreKey,
      });

      if (result.scdEvent) {
        result.label = renameSCDProp(result.name);
      }

      return result;
    });

    if (this.props.menuItemTypes?.includes(MenuItemType.SCDProperty)) {
      items = items.filter((item) => isScdPropertyName(item.name));
    }

    return items;
  }

  private isPropertyDisabled(property, propertyValue) {
    if (property?.customPropertyId && !isPersistedCustomProperty(property?.customPropertyId)) {
      return false;
    }

    if (isScdPropertyName(property.name) && this.isScdPropertyDisabled(property).disabled) {
      return true;
    }

    return Boolean(
      Object.prototype.hasOwnProperty.call(this.props.disabledItems, propertyValue) ||
        (!this.props.selectableSensitiveProperties && !this.isPropertyAccessible(property)),
    );
  }

  private isScdPropertyDisabled(
    property: any,
    shouldDisableUnselectedScd?: boolean,
  ): {disabled: boolean; disabledReason?: string} {
    if (shouldDisableUnselectedScd) {
      return {disabled: true, disabledReason: SCD_PROPERTY_DISABLED_REASON.DISABLED_DUE_TO_SCD_LIMIT};
    }
    if (this.props.disableAllScdProps) {
      return {disabled: true, disabledReason: SCD_PROPERTY_DISABLED_REASON.DISABLED_SCD_IN_DATA_VIEW_FILTERS};
    }

    if (isInvalidScdPropertyType(property.type)) {
      return {disabled: true, disabledReason: SCD_PROPERTY_DISABLED_REASON.getDisabledDueToPropertyType(property.type)};
    }

    // Disable scd property if outside of user mode or property's data group id and given data group id are different
    const propertyDataGroupId = property.dataGroupId ?? ``;
    let reportDataGroupId = ``;

    if (this.props.dataGroupIds) {
      // If there are multiple data groups (of any kind), disable SCD property
      if (this.props.dataGroupIds.size > 1) {
        return {
          disabled: true,
          disabledReason: SCD_PROPERTY_DISABLED_REASON.DISABLED_DUE_TO_MULTIPLE_DATA_GROUPS,
        };
      } else if (this.props.dataGroupIds.size === 1) {
        // If there's exactly one data group, make sure property matches it below
        reportDataGroupId = getSingleDataGroupId(this.props.dataGroupIds) || ``;
      }
    }

    const bypassDataGroupMismatch = this.props.enableAllDataGroupsForScdProps || reportDataGroupId === ``;
    if (!bypassDataGroupMismatch && reportDataGroupId !== propertyDataGroupId) {
      const dataGroupName = this.state.dataGroups[reportDataGroupId]?.displayName || `User`;
      return {
        disabled: true,
        disabledReason: SCD_PROPERTY_DISABLED_REASON.getDisabledDueToMismatchingDataGroup(dataGroupName),
      };
    }

    return {disabled: false};
  }

  private isPropertyAccessible(property) {
    return this.props.currentProject.get().value?.hasSensitiveAccess || !property.sensitive;
  }

  private isPropertyRecentlyUsed(property: PropertySectionItem | Item) {
    if (!this._recentlyUsedUniqueKeys) {
      this._recentlyUsedUniqueKeys = new Set(
        [ResourceType.Event, ResourceType.User, ResourceType.Cohort].flatMap((resourceType) => {
          return this.getRecentItems(resourceType).map((item) => {
            return getItemUniqueKey({...item, resourceType});
          });
        }),
      );
    }
    return this._recentlyUsedUniqueKeys.has(getItemUniqueKey(property));
  }

  private getPropertyDefinitionsForEventsStoreKey(): string {
    return eventDefinitionParamsAndKey(this.eventNames(), {
      includeDimensionProperties: false,
      projectId: this.sourceAttrs.projectId.toString(),
      workspaceId: this.sourceAttrs.workspaceId.toString(),
    }).key;
  }

  /**
   * This function basically massages a variety of inputs into a normalized "PropertySectionItem".
   *
   */
  // TODO opslead
  private getPropertyMenuItem({
    property,
    dataGroupId,
    resourceType,
    selected,
    propertyDefaultType,
    propertyDefinitionsForEventsStoreKey,
    isDisplayingMultipleResources,
  }: {
    property: CohortItem | PropertySectionItem | PropertyDefinition | SpecialControl | Selection;
    dataGroupId?: string;
    resourceType?: ResourceType;
    selected?: any;
    propertyDefaultType?: PropertyType;
    // Since computing the key is expensive, it can be passed in as a perf optimization if this method is called for
    // a list of property items
    propertyDefinitionsForEventsStoreKey?: string;
    isDisplayingMultipleResources: boolean;
  }): PropertySectionItem {
    const forCohorts = resourceType === ResourceType.Cohort;

    if (`resourceType` in property && property.resourceType) {
      // custom properties/formulas specify their own resource types
      resourceType = property.resourceType;
    }
    selected = selected || this.state.selected;

    // if we are in an inner menu then we can look into the selected behavior for the property
    if (this.state.stagedItemsStack.length && this.state.selected?.behavior) {
      selected = this.state.selected?.behavior?.property;
    } else if (this.state.selected?.behavior) {
      // if we are in the top level menu with a selected behavior, don't highlight the nested
      // property in the list of top level properties
      selected = null;
    }

    const subLabelTheme = `subLabelTheme` in property && property.subLabelTheme;
    const isControlItem = `isControlItem` in property && property.isControlItem;
    const customPropertyId = `customPropertyId` in property && property.customPropertyId;
    const customProperty = `customProperty` in property && property.customProperty;

    // TODO: this is kind of crazy
    let name = `name` in property && property.name;
    const value = (`value` in property && property.value) || name || (customProperty && customProperty.name);
    let label = `label` in property && property.label;
    label = label || value;
    name = name || value;

    propertyDefinitionsForEventsStoreKey =
      propertyDefinitionsForEventsStoreKey ?? this.getPropertyDefinitionsForEventsStoreKey();
    const dataDefinition = (propertyDefinitionsForEventsStore?.value[propertyDefinitionsForEventsStoreKey] || {})[name];

    const customPropertyIdMatches = selected?.customPropertyId && selected.customPropertyId === customPropertyId;
    const dimensionDataGroupIdMatches =
      (selected?.dataGroupId && `dataGroupId` in property && selected.dataGroupId === property.dataGroupId) ||
      (selected?.tempDataGroupId && `dataGroupId` in property && selected.tempDataGroupId === property.dataGroupId);
    const dimensionPropertyMatches =
      dimensionDataGroupIdMatches &&
      (!selected?.customPropertyId || customPropertyIdMatches) &&
      [selected.value, `value` in property && property.value].every(Array.isArray) &&
      selected.value?.[0] === (`value` in property && property.value?.[0]) &&
      selected.value?.[1] === (`value` in property && property.value?.[1]);
    const bothAreJoinKeys =
      selected?.joinPropertyType &&
      `joinPropertyType` in property &&
      property.joinPropertyType &&
      !Array.isArray(selected.value) &&
      // property.value is currently just omitted for join keys, but testing if it's an array in case that changes
      `value` in property &&
      !Array.isArray(property.value);
    // selected.dataGroupId is currently omitted for join keys; joinPropertyType is the only way to tell it's a dimension prop
    const noDimensionDataGroup =
      !selected?.joinPropertyType && !(`joinPropertyType` in property && property.joinPropertyType);
    const isSelected =
      (forCohorts && this.isItemSelected(value, selected)) ||
      (Array.isArray(selected) && this.isItemSelected(value, selected)) ||
      (selected && dataGroupId && dataGroupId === selected.dataGroupId && selected.value === value) ||
      dimensionPropertyMatches ||
      (selected &&
        (noDimensionDataGroup || bothAreJoinKeys) &&
        selected.value === value &&
        selected.resourceType === resourceType &&
        selected.dataGroupId === dataGroupId) ||
      (noDimensionDataGroup && (noDimensionDataGroup || bothAreJoinKeys) && customPropertyIdMatches) ||
      false;
    const appliedDataGroupId = `dataGroupId` in property && property.dataGroupId ? property.dataGroupId : dataGroupId;

    const type = this.getPropertyType(name, property, isSelected && selected);
    const propertyType =
      propertyDefaultType || (isSelected && selected.propertyDefaultType) || (`type` in property && property.type);
    const isScd = `scdEvent` in property && !!property.scdEvent;
    // query api has limit for only 5 SCD / borrowed props
    const hasReachedSCDLimit = Array.isArray(selected)
      ? selected.filter((s) => s.scdEvent).length >= SCD_COLUMN_SELECTION_LIMIT
      : false;
    const shouldDisableUnselectedScd = hasReachedSCDLimit && isScd && !isSelected;
    const isDisabled = this.isPropertyDisabled(property, value) || shouldDisableUnselectedScd;
    const isAccessible = this.isPropertyAccessible(property);
    let tooltip: TooltipInfo = null;
    if (!isAccessible) {
      tooltip = {text: DISABLED_DUE_TO_CLASSIFIED_DATA};
    } else if (isScd && isDisabled) {
      tooltip = {
        text: this.isScdPropertyDisabled(property, shouldDisableUnselectedScd).disabledReason,
        'max-width': `200px`,
      };
    }

    const isDimension = type === PropertyType.Dimension;
    const isObject = type === PropertyType.Object;
    const includeLookupTableProperties = isDimension && this.canStageItemType(StagedItemType.Dimension);

    const allowPill = this.props.allowTypecastPillAction === TypecastPillAction.Typecast;
    const canTypecastProperty = !forCohorts && !isDimension && !isControlItem && isAccessible;

    let menuItemType;
    if (isDimension) {
      menuItemType = MenuItemType.LookupTableProperties;
    } else {
      switch (resourceType) {
        case ResourceType.Event:
          if (customProperty || customPropertyId) {
            menuItemType = MenuItemType.EventCustomProperties;
          } else {
            menuItemType = MenuItemType.EventProperties;
          }
          break;
        case ResourceType.User:
          if (customProperty || customPropertyId) {
            menuItemType = MenuItemType.UserCustomProperties;
          } else if (appliedDataGroupId) {
            menuItemType = MenuItemType.DataGroupProperties;
          } else {
            menuItemType = MenuItemType.UserCustomProperties;
          }
          break;
        case ResourceType.Cohort:
          menuItemType = MenuItemType.Cohorts;
          break;
        case ResourceType.Other:
          menuItemType = MenuItemType.Other;
          break;
        case ResourceType.Formula:
        case ResourceType.Metric:
          throw Error(`Unexpected resource type in getPropertyMenuItem: ${resourceType}`);
        default:
          assertUnreachable(resourceType);
      }
    }

    const basePropertySectionItem: SectionItem<any, PropertyItemState> = {
      ...(`dimensionDataGroupId` in property && property.dimensionDataGroupId
        ? {dimensionDataGroupId: property.dimensionDataGroupId}
        : {}),
      ...(`tooltipData` in property && property.tooltipData ? {tooltipData: property.tooltipData} : {}),
      hasCaret:
        (includeLookupTableProperties || isObject || this.props.showItemsCaret) &&
        !isControlItem &&
        isAccessible &&
        !this.useMultipleSelection(),
      persistCaret: isObject && !this.useMultipleSelection(),
      caretStyle: `arrow`,
      hasPill: allowPill && canTypecastProperty,
      hidden: !!(`hidden` in property && property.hidden),
      icon:
        (`icon` in property && property.icon) ||
        (forCohorts &&
          !(this.state.selectedFilter?.resourceType === ResourceType.Cohort && this.useMultipleSelection()) &&
          `community`) ||
        getIconForPropertyType(type, {
          isCustom: Boolean(customProperty),
          isScd,
        }),
      isControlItem,
      isDisabled,
      ...(`scdEvent` in property && property.scdEvent
        ? {
            scdEvent: property.scdEvent,
          }
        : {}),
      ...(`scdOptions` in property && property.scdOptions
        ? {
            scdOptions: property.scdOptions,
          }
        : {}),
      tooltip,
      isSelected,
      label,
      name,
      pillAction: PropertyItemAction.Typecast,
      pillLabel: `Typecast`,
      densityLocal: `densityLocal` in property && property.densityLocal,
      densityGlobal: `densityGlobal` in property && property.densityGlobal,
      count: `count` in property && property.count,
      uiQueryCount: `uiQueryCount` in property && property.uiQueryCount,
      uiUniqueQueryCount: `uiUniqueQueryCount` in property && property.uiUniqueQueryCount,
      ...(`joinPropertyLabel` in property && property.joinPropertyLabel
        ? {subLabel: property.joinPropertyLabel}
        : {subLabel: `subLabel` in property && property.subLabel}),
      isLookupTableProperty: `isLookupTableProperty` in property && property.isLookupTableProperty,
      ...(subLabelTheme ? {subLabelTheme} : {}),
      menuItemType,
    };

    let propertyItem: PropertySectionItem;

    if (resourceType === ResourceType.Cohort) {
      let item: Item;
      if (typeof value === `number`) {
        item = {
          resourceType: ResourceType.Cohort,
          label: this.props.i18n.print(label),
          ...((property as CohortItem).dataGroupId ? {data_group_id: (property as CohortItem).dataGroupId} : {}),
          value,
        };
      } else if (value) {
        const cohortDef = value as InlineCohortSegment;
        item = {
          resourceType: ResourceType.Cohort,
          label: this.props.i18n.print(label),
          value: cohortDef,
        };
      }
      // TODO: I'm not sure this is the right thing to do here (putting these on the PropertySectionItem)
      // but we use this data when generating the cohort tooltip so keeping it this way for now. For properties,
      // we look up the definition data from the store inside the util.
      // Prior to this change we were just blindly expanding `{...property}` which originally is of type Cohort
      // in this case. I'd rather be explicit about what we're doing and why.
      const cohortExtras: CohortExtras = {
        description: `description` in property && property.description,
        canView: `canView` in property && property.canView,
        canUpdateBasic: `canUpdateBasic` in property && property.canUpdateBasic,
        canShare: `canShare` in property && property.canShare,
        isSuperadmin: `isSuperadmin` in property && property.isSuperadmin,
      };

      propertyItem = Object.assign(basePropertySectionItem, item, cohortExtras);
    } else {
      const item = {
        ...(appliedDataGroupId ? {dataGroupId: appliedDataGroupId} : {}),
        ...(customPropertyId ? {customPropertyId} : {}),
        ...(customProperty ? {customProperty} : {}),
        ...(`tempDataGroupId` in property && property.tempDataGroupId
          ? {tempDataGroupId: property.tempDataGroupId}
          : {}),
        ...(`dimensionDataGroupId` in property && property.dimensionDataGroupId
          ? {dimensionDataGroupId: property.dimensionDataGroupId}
          : {}),
        ...(`tooltipData` in property && property.tooltipData ? {tooltipData: property.tooltipData} : {}),
        ...(`joinPropertyType` in property && property.joinPropertyType
          ? {joinPropertyType: property.joinPropertyType}
          : {}),
        ...(`scdEvent` in property && property.scdEvent ? {scdEvent: property.scdEvent} : {}),
        ...(`scdOptions` in property && property.scdOptions ? {scdOptions: property.scdOptions} : {}),
        label,
        propertyDefaultType: isObject ? PropertyType.Object : propertyType,
        resourceType,
        type,
        value,
        ...(`joinPropertyLabel` in property && property.joinPropertyLabel
          ? {subLabel: property.joinPropertyLabel}
          : {subLabel: `subLabel` in property && property.subLabel}),
      };
      propertyItem = Object.assign(basePropertySectionItem, item);
    }

    if (isProperty(propertyItem) || isCohort(propertyItem)) {
      const isProfilePropertyMenu = !this.shouldShowEventProps();
      const propertyDataGroupId = (propertyItem.resourceType === ResourceType.User && propertyItem.dataGroupId) || null;
      const selectedDataGroupId = getSingleDataGroupId(this.props.dataGroupIds) || null;
      const isPropertyForSelectedProfileType =
        propertyItem.resourceType === ResourceType.User &&
        propertyDataGroupId === selectedDataGroupId &&
        (!this.props.dataGroupIds || this.props.dataGroupIds.size < 2);
      const isShowingAll = this.state.selectedFilter?.value === `all`;
      const includeResourceLabel = isShowingAll && (!isProfilePropertyMenu || !isPropertyForSelectedProfileType);
      propertyItem.subLabel = getPropertyPathPrefix(
        propertyItem,
        this.props.i18n,
        Object.values(this.state.dataGroups),
        {
          includeResourceLabel,
        },
      );
      propertyItem.excludeFromInputMatch = resourceType !== ResourceType.Event;
    }
    propertyItem.subLabelTheme = SubLabelTheme.Breadcrumb;
    // we put label in here twice so that a user could do fuzzy matching in either order (label then sublabel or sublabel then label)
    // Imagine a lookup table path like "email.domain", a user can do "domemail" or "emaildom" to match it.
    propertyItem.searchableContent = [
      dataDefinition?.name,
      propertyItem.label,
      propertyItem.subLabel,
      propertyItem.label,
    ].join(` `);

    propertyItem.isRecentlyUsed = this.isPropertyRecentlyUsed(propertyItem);

    if (!isDisplayingMultipleResources) {
      propertyItem.subLabel = null;
    }

    return propertyItem;
  }

  public getSearchFilter(): string {
    return this.state.searchFilter;
  }

  private getPropertyType(
    name: string,
    property: CohortItem | PropertySectionItem | PropertyDefinition | SpecialControl | Selection,
    selected?: PropertySectionItem,
  ): PropertyType {
    const type = `type` in property && property.type;

    if (selected) {
      if (`joinPropertyType` in selected && selected.joinPropertyType === PropertyType.Object) {
        return PropertyType.Object;
      }

      if (`type` in selected && selected.type && type !== PropertyType.Dimension) {
        return selected.type;
      }
    }

    const castToString =
      STRING_TYPECAST_PROPERTIES.includes(name) ||
      (this.props.listProperty && [PropertyType.List, PropertyType.Object].includes(type));

    return castToString ? PropertyType.String : type;
  }

  getArraySelection() {
    const selected = this.state.selected;

    if (Array.isArray(selected)) {
      return selected;
    }

    return [];
  }

  private isDimensionSelected(selected) {
    if (!selected) {
      return false;
    }
    // lookup table (aka dimension property) selected.value is an array of two strings (vs b2b profiles)
    return (selected.dataGroupId || selected.tempDataGroupId) && Array.isArray(selected.value);
  }

  private isItemSelected(value, selection: Selection) {
    const getCompareValue = (v) => (v?.cohort?.raw_cohort ? v.cohort.raw_cohort.unsavedId : v);

    if (Array.isArray(selection)) {
      return selection.some((item) => getCompareValue(item.value) === getCompareValue(value));
    }

    return selection && getCompareValue(selection.value) === getCompareValue(value);
  }

  private async editCohort(item) {
    const cohortParsed = item.id ? cohortsStore.value[item.id] : item.value?.cohort?.raw_cohort || item.value?.cohort;
    const cohort = CohortModel.parseCohort(cohortParsed);
    const {cohortStagedValue, cohortSavedValue} = await this.promptInlineCohortsModal(cohort);

    this.handleCohortUpdate(cohortStagedValue, cohortSavedValue);
    if (cohortStagedValue?.cohort || cohortSavedValue?.cohort) {
      this.trackEvent(TrackingEvent.OpenInlineCohortsModal, {
        action: `Edit`,
        context: this.props.context,
      });
    }
  }

  private trackInlineCohorts(name, isSaved, isEditingTemporaryCohort, isEditingExistingCohort, savedAsNew) {
    if (isSaved) {
      this.trackEvent(TrackingEvent.SaveInlineCohorts, {
        context: this.props.context,
        name,
        // Indicates whether an applied inline cohort was later saved
        converted: !!isEditingTemporaryCohort,
        savedAsNew,
      });
    } else if (!isEditingTemporaryCohort) {
      this.trackEvent(TrackingEvent.CreateInlineCohort, {
        context: this.props.context,
        name,
      });
    } else {
      this.trackEvent(TrackingEvent.EditInlineCohorts, {
        context: this.props.context,
        name,
        existing: isEditingExistingCohort,
      });
    }
  }

  // add cohort to the selected attr, either replace an existing selected cohort, or add a new cohort
  // to the selected properties menu
  private replaceOrSelectCohort(item: PropertySectionItem, isEditingCohort: boolean, unsavedId: string) {
    const selected = this.state.selected;

    // based on internal user feedback cohorts created inline
    // should be committed much like custom props with breakdowns
    const index = Array.isArray(selected)
      ? this.getArraySelection().findIndex(
          (selectedItem) =>
            (isInlineCohort(selectedItem) && selectedItem.value.cohort?.raw_cohort?.unsavedId === unsavedId) ||
            selectedItem.value === item.value,
        )
      : -1;

    // @ts-ignore
    if (isEditingCohort && selected && index > -1) {
      this.replaceItem(item, index, {commit: true});
    } else {
      this.selectItem(item, {commit: true});
    }
  }

  private getCreatedBy(): User {
    return {
      id: this.getUserId(),
      name: userDetailsStore?.value.name,
      email: userDetailsStore?.value.email,
    };
  }

  private getSavedCohortSelection(): SavedCohortItem[] {
    const selected = this.state.selected;
    const cohorts = Array.isArray(selected) ? selected : [selected];

    return cohorts.filter(
      (item): item is SavedCohort => item?.resourceType === ResourceType.Cohort && Number.isInteger(item?.value),
    );
  }

  private getInlineCohortSelection(): InlineCohort[] {
    const selected = this.state.selected;
    const cohorts = Array.isArray(selected) ? selected : [selected];

    return cohorts.filter(
      (item): item is InlineCohort =>
        !!(isInlineCohort(item) && item.value && item.value.cohort && item.value.cohort.raw_cohort),
    );
  }

  private getInlineCohortItems() {
    return this.getInlineCohortSelection().map((item) => ({
      ...item,
      createdBy: item.value.cohort.raw_cohort.createdBy,
      resourceType: ResourceType.Cohort,
    }));
  }

  private getSelectedCustomFormula(resourceType) {
    let selected = this.state.selected || [];
    if (!Array.isArray(selected)) {
      selected = [selected];
    }

    return selected.filter(
      (item) =>
        isProperty(item) &&
        item.resourceType === resourceType &&
        item.customPropertyId &&
        !isPersistedCustomProperty(item.customPropertyId),
    );
  }

  private registerTempLookupTableMappingFromSelected() {
    const selected = this.state.selected;
    if (!Array.isArray(selected) && isProperty(selected)) {
      const tempDataGroupId = selected?.tempDataGroupId;
      if (tempDataGroupId) {
        registerTempLookupTableMapping(selected, tempDataGroupId, this.props.i18n);
      }
    }
  }

  private addTempMappingsToProperties(
    resourceType: ResourceType.Event | ResourceType.User,
    custom: boolean,
    storeProperties,
  ) {
    const mappings = getTempLookupTableMappings(resourceType, custom);
    return storeProperties.map((storeProperty) => {
      // storeProperty is of type PropertyItem ('name' field used, 'value' field not used for name),
      // but the store is typed as Record<string,any>
      const value = custom ? storeProperty.customPropertyId : storeProperty.name;
      if (mappings.has(value)) {
        const tempDataGroupId = mappings.get(value);
        const joinPropertyType = storeProperty.joinPropertyType || storeProperty.type;
        return {...storeProperty, tempDataGroupId, type: PropertyType.Dimension, joinPropertyType};
      }
      return storeProperty;
    });
  }

  private filterMultiAttributionProperties<T extends {type?: PropertyType; joinPropertyType?: PropertyType}>(
    items: T[],
  ): T[] {
    return items.filter(
      (item) =>
        item.type === PropertyType.String ||
        (item.type === PropertyType.Dimension && item.joinPropertyType === PropertyType.String),
    );
  }

  private getSectionsForStagedItem(stagedItemType: StagedItemType, item): CombinedSection[] {
    let sections = [];
    switch (stagedItemType) {
      case StagedItemType.Typecast:
        sections = this.getTypecastMenuSections(item) as PropertySection[];
        break;
      case StagedItemType.Datetime:
        sections = this.getDatetimeMenuSections() as PropertySection[];
        break;
      case StagedItemType.Dimension:
        sections = this.getDimensionMenuSection(item);
        break;
      case StagedItemType.Object:
      case StagedItemType.ComputedListProperty:
      case StagedItemType.ComputedListNumericProperty:
        sections = this.getObjectMenuSection();
        break;
      case StagedItemType.User:
        sections = this.getUserPropertiesMenuSections();
        break;
      case StagedItemType.Group:
        sections = this.getGroupMenuSection(item);
        break;
      case StagedItemType.Cohort:
        sections = this.getCohortsMenuSections();
        break;
      case StagedItemType.BehaviorWithEvent:
      case StagedItemType.BorrowEvent:
        sections = this.getStagedEventsMenuSections(item);
        break;
      case StagedItemType.BehaviorMultiAttribution:
        sections = this.getEventPropertiesMenuSections({isForMultiAttribution: true, hideSectionLabel: true});
        break;
      case StagedItemType.PropertiesForEvent:
        sections = this.getEventPropertiesMenuSections({item});
        break;
      case StagedItemType.Experiment:
        sections = this.getExperimentsMenuSections();
        break;
      default:
        assertUnreachable(stagedItemType);
    }

    sections[0]?.items.unshift({
      icon: `arrow-left`,
      label: this.t(`query-builder.properties-menu.go-back`),
      isSpecialAction: true,
      excludeFromInputMatch: true,
      value: SpecialActions.NavigateBack,
    });

    return sections;
  }

  async createCustomProperty(resourceType: ResourceType, forBehavioralProperty: boolean) {
    const props = {resourceType};
    const {appliedValue, deletedId, didClose, hasGroupProperty} = await this.promptCustomPropertiesModal(
      props,
      forBehavioralProperty,
    );
    if (appliedValue) {
      this.applyCustomProperty(appliedValue, null);
    } else if (didClose) {
      this.props.onModalClose?.();
    } else if (deletedId) {
      this.onDeletedCustomProperty();
    }
    this.trackEvent(TrackingEvent.CreateCustomProperty, {
      context: this.props.context,
      name: appliedValue?.name,
      ...props,
      hasGroupProperty,
    });
  }

  async createInlineCohort() {
    this.trackEvent(TrackingEvent.OpenInlineCohortsModal, {
      action: `Create`,
      context: this.props.context,
    });
    // ok to use getSingleDataGroupId because create cohort option is hidden for multi data group queries
    const dataGroupId = getSingleDataGroupId(this.props.dataGroupIds);
    const newDefaultCohort = new CohortModel({...new CohortModel(), dataGroupId});
    const {cohortStagedValue, cohortSavedValue} = await this.promptInlineCohortsModal(newDefaultCohort);
    this.props.onModalClose?.();
    this.handleCohortUpdate(cohortStagedValue, cohortSavedValue);
  }

  // Attribute Helpers

  private canAccessUserProperties() {
    return this.props.projectHasPeople;
  }

  /**
   * TODO for perf purposes, infra team does not want standard people props list available to filter when a
   * B2B data group has been selected for the main analysis. Hopefully we can remove this restriction
   * eventually, as there are legit analysis cases that it enables.
   */
  private shouldShowUserProps() {
    return (
      (this.canAccessUserProperties() || this.props.menuItemTypes?.includes(MenuItemType.UserProperties)) &&
      !this.hasSelectedDataGroup() &&
      ![StagedItemType.BehaviorMultiAttribution, StagedItemType.Experiment].includes(this.state.stagedItemType)
    );
  }

  private shouldShowEventProps() {
    return !this.props.hideEvents;
  }

  private hasSelectedDataGroup() {
    return (
      this.props.allDataGroups &&
      this.props.dataGroupIds &&
      Array.from(this.props.dataGroupIds).some((id) => id !== null)
    );
  }

  private shouldShowExperiments() {
    // hide experiments when there are multiple data groups
    if (this.props.dataGroupIds?.size > 1) {
      return false;
    }

    const reportName = this.props.trackName ?? getReportName();
    return (
      !this.props.hideExperiments &&
      // Flows reports have support for experiment filters but not breakdowns
      (reportName !== ReportName.Flows || this.props.context === `filter`) &&
      // Impact app uses a special API that doesn't support experiments
      reportName !== ReportName.Impact &&
      !!this.experimentNames &&
      !isEmpty(this.experimentNames)
    );
  }

  private shouldShowCohorts() {
    // hide cohorts when there are multiple data groups
    if (this.props.dataGroupIds?.size > 1) {
      return false;
    }

    return (
      this.props.inlineCohortsOverride?.enable ||
      (this.props.projectHasCohorts &&
        (!this.hasSelectedDataGroup() || this.props.supportB2bCohorts) &&
        this.state.stagedItemType !== StagedItemType.BehaviorMultiAttribution)
    );
  }

  private shouldFilterToEventPropertyIntersection() {
    const propertyCombineMode = this.props.propertyCombineMode || PropertyCombineMode.Union;
    return (
      this.shouldShowEventProps() &&
      propertyCombineMode === PropertyCombineMode.Intersection &&
      !isQueryOnAllEvents(this.eventNames())
    );
  }

  private canCreateCustomEventProperties(): boolean {
    return (
      this.shouldShowEventProps() &&
      this.canCreateEntity() &&
      this.props.includeCustomProperties !== IncludeCustomPropertyType.None
    );
  }

  private canCreateCustomUserProperties(): boolean {
    return (
      this.shouldShowUserProps() &&
      this.canCreateEntity() &&
      this.props.includeCustomProperties !== IncludeCustomPropertyType.None
    );
  }

  private canCreateCustomGroupProperties(): boolean {
    return (
      this.hasSelectedDataGroup() &&
      // only show for single datagroup queries
      this.getNonUserdataGroupIds().length < 2 &&
      this.state.stagedItemType !== StagedItemType.BehaviorMultiAttribution &&
      this.canCreateEntity() &&
      this.props.includeCustomProperties !== IncludeCustomPropertyType.None
    );
  }

  private canCreateInlineCohorts(): boolean {
    // Create Cohort availability will always be overriden by the inline-cohorts-override settings
    return this.shouldShowCohorts() && Boolean(this.props.inlineCohortsOverride?.enable ?? this.canCreateEntity());
  }

  private canCreateEntity(): boolean {
    if (this.props.hideEditControls) {
      return false;
    }

    return ![
      StagedItemType.Datetime,
      StagedItemType.Dimension,
      StagedItemType.Object,
      StagedItemType.Typecast,
    ].includes(this.getStagedItemType());
  }

  public isUnsortableStage() {
    return (
      (this.hasStagedItem() &&
        ![
          StagedItemType.BehaviorWithEvent,
          StagedItemType.BehaviorMultiAttribution,
          StagedItemType.PropertiesForEvent,
          StagedItemType.BorrowEvent,
        ].includes(this.state.stagedItemType)) ||
      this.props.listProperty
    );
  }

  public canSortItems(): boolean {
    // getSortedMenuSections is some pattern to enforce sorting on a query-block
    // this completely contradicts our idea of sorting on behalf of the caller.
    // TODO: Research why we need getSortedMenuSections and if we can pull it out
    return !this.props.getSortedMenuSections && !this.isUnsortableStage();
  }

  private canWriteDataDefinition() {
    return (
      this.props.currentProject.get().value?.hasPermission(Permission.WriteDataDefinitions) ||
      this.state.permissionList.includes(Permission.WriteDataDefinitions)
    );
  }

  /**
   * This determines if a stagedItemType should be staged as a menu or treated as a selectable item.
   * This logic should be dependent on the context of the menu and not the item itself.
   */
  private canStageItemType(stagedItemType: StagedItemType): boolean {
    switch (stagedItemType) {
      case StagedItemType.Datetime:
        return this.props.showSpecialDatePropertyUnit;
      case StagedItemType.Typecast:
        return this.props.allowTypecastPillAction === TypecastPillAction.Typecast;
      case StagedItemType.Dimension:
        return !this.props.excludeLookupTableProperties;
      default:
        return true;
    }
  }

  /**
   * Checks if the selected property is persisted or not
   * in the case for custom properties and inline cohorts
   */
  public isUnsavedCustomEntity(selected: Selection = this.state.selected) {
    const isUnsavedEntity = (item: Item) => {
      if (isInlineCohort(item)) {
        return Boolean(item.value?.cohort?.raw_cohort);
      }

      if (isProperty(item)) {
        return item.customPropertyId && !isPersistedCustomProperty(item.customPropertyId);
      }

      return false;
    };

    if (Array.isArray(selected)) {
      return selected.some(isUnsavedEntity);
    }

    return isUnsavedEntity(selected);
  }

  /**
   * Decides if the apply button should be shown.
   */
  public shouldShowApplyButton() {
    return this.useMultipleSelection();
  }

  /**
   * Decides if the apply button should be in a disabled state.
   */
  public getApplyButtonDisabledReason(): ApplyButtonDisabledReason {
    const minSelection = this.props.minSelection || 0;

    if (this.getArraySelection().length < minSelection) {
      return ApplyButtonDisabledReason.TooFewSelected;
    } else if (this.props.maxSelection && this.getArraySelection().length > this.props.maxSelection) {
      return ApplyButtonDisabledReason.TooManySelected;
    }

    return ApplyButtonDisabledReason.None;
  }

  /**
   * Gets the message to show when the apply button is disabled.
   */
  public getApplyButtonDisabledMessage(): Translatable {
    const reason = this.getApplyButtonDisabledReason();
    switch (reason) {
      case ApplyButtonDisabledReason.TooFewSelected:
        return this.t(`query-builder.properties-menu.select-under-limit`, {count: this.props.minSelection});
      case ApplyButtonDisabledReason.TooManySelected:
        return this.t(`query-builder.properties-menu.select-over-limit`, {count: this.props.maxSelection});
      default:
        return null;
    }
  }

  /**
   * Decides if the Select All button should be disabled if shown.
   */
  public isSelectAllDisabled() {
    const prop = this.props.selectAllDisabled;
    const mechanism = this.props.selectAllMechanism;

    // if prop is explicitly set, use that value
    if (prop) {
      return prop;
    }

    // disable Deselect All if there is none to deselect
    if (mechanism === SelectAllMechanism.Partial) {
      const deselectableItems = this.getArraySelection().filter((item) => !this.isPropertyDisabled(item, item.value));

      return deselectableItems.length === 0;
    }

    return false;
  }

  /**
   * When interacting with a list of objects property, we present dynamically
   * computed properties on the list like sum of values, length and distinct count of values
   */
  getComputedListProperties(): PropertySection {
    // prevents nesting computed properties
    if (
      this.state.stagedItemsStack.some((item) =>
        [StagedItemType.ComputedListProperty, StagedItemType.ComputedListNumericProperty].includes(item.stagedItemType),
      )
    ) {
      return null;
    }

    return {
      label: this.t(`query-builder.properties-menu.sidebar-computed`),
      items: [LIST_LENGTH, LIST_PROPERTY_SUM, LIST_PROPERTY_UNIQUES].map((item) => ({
        ...item,
        // makes sure for LIST_LENGTH, the resource type matches the list item property
        resourceType: this.props.listProperty.resourceType,
      })),
    };
  }

  /**
   * For menus working with multi-select, provides an interaction for unchecking
   * all currently selected values, without commiting the updated selection
   */
  clearArraySelection(): void {
    const excludeClearProperties = this.props.excludeClearProperties || [];
    this.setState(({selected}) => ({
      selected: selected?.filter((item) => excludeClearProperties.includes(item.value)),
    }));
  }

  isDisplayingMultipleResources(): boolean {
    return this.getFilterOptionSectionItemsForSidenav().length > 2;
  }

  render() {
    const createSectionItems = this.getCreateSectionItems();

    const showLoadingSkeleton = this.state.isLoading;

    const LoadingContent = (
      <SkeletonListReact
        className='mp-properties-menu-skeleton-list'
        numRows={12}
        rowSize={SkeletonListRowSize.Large}
      />
    );

    const sidenavItems = this.getFilterOptionSectionItemsForSidenav();

    return (
      <div
        ref={this.rootEl}
        className={clsx(`mp-properties-menu`, `mp-child-menu`, {'is-loading': showLoadingSkeleton})}
        style={{height: getChildMenuHeight(), width: this.props.customWidth || 520}}
      >
        {this.props.open && (
          <>
            <MenuSearchBar
              ref={this.searchInputEl}
              value={this.state.searchFilter}
              handleSearchInput={(searchFilter) => this.debouncedSearchInput(searchFilter)}
            >
              {createSectionItems.length > 0 ? (
                createSectionItems.length === 1 ? (
                  <ButtonReact
                    className='create-custom-button'
                    theme={ButtonTheme.Tertiary}
                    icon='plus'
                    tooltip={{text: createSectionItems[0].label, placement: Placement.Top}}
                    onClick={() => this.showCreateItemModal(createSectionItems[0].value)}
                  >
                    {this.t(`query-builder.create`)}
                  </ButtonReact>
                ) : (
                  <SelectReact
                    className='create-custom-button'
                    elref='createCustomPropertySelect'
                    disableSearch
                    showPreviewTooltip={false}
                    sections={[{items: createSectionItems}]}
                    theme={SelectTheme.Tertiary}
                    icon={{leftIcon: `plus`}}
                    size={ButtonSize.Medium}
                    label={this.t(`query-builder.create`)}
                    hideInputFocusStyling
                    menuWidth={240}
                    tooltip={{text: this.t(`query-builder.properties-menu.create-tooltip`), placement: Placement.Top}}
                    onChange={(ev) => this.showCreateItemModal(ev.detail.item.value)}
                  />
                )
              ) : null}
            </MenuSearchBar>
            <div className='mp-properties-menu-wrapper'>
              {/* side navigation */}
              {sidenavItems?.length > 0 && (
                <ResizablePanelReact
                  className='mp-properties-menu-sidenav-panel'
                  storageKey='properties-menu-sidebar'
                  defaultSize={150}
                  minSize={150}
                  maxSize={300}
                  disableCollapsing
                  panelName={PanelName.Properties}
                  defaultAlignment={Direction.Left}
                  containerEl={this.rootEl.current?.parentElement}
                >
                  {this.state.isLoadingDataGroups ? (
                    LoadingContent
                  ) : (
                    <ItemsMenuReact
                      tabIndex={0}
                      size={ItemsMenuSize.Small}
                      disableFirstItemPadding
                      sections={[{items: sidenavItems, selectedTheme: SelectedTheme.Focus}]}
                      onClickItem={(ev) => this.handleFilterChange(ev.detail.item.value)}
                    />
                  )}
                </ResizablePanelReact>
              )}
              <div className='mp-properties-menu-content'>
                <div className='mp-properties-menu-header'>{this.props.menuHeader}</div>
                {showLoadingSkeleton ? (
                  LoadingContent
                ) : (
                  <ItemsMenuReact
                    className='mp-properties-menu-items-menu'
                    key={
                      this.hasStagedItem()
                        ? `stageditem:${this.peekStagedItem().label}`
                        : `tab:${this.state.selectedFilter?.resourceType}`
                    }
                    elref='mainSelectionMenu'
                    emptyMessage={this.t(`query-builder.no-items`)}
                    searchFilter={this.getSearchFilter()}
                    multiple={this.useMultipleSelection()}
                    selectAllMechanism={this.props.selectAllMechanism}
                    showSelectAll={this.props.showSelectAll}
                    selectAllDisabled={this.isSelectAllDisabled()}
                    projectIsFreePlan={this.props.projectIsFreePlan}
                    disableSearchSort
                    disableFirstItemPadding
                    contextPanelAlignment={this.props.contextPanelAlignment}
                    onClick={(ev) => ev.stopPropagation()}
                    onClickExpanderItem={this.handleExpandItemClick.bind(this)}
                    onClickItem={this.handleClickItem}
                    onClickPill={(ev) => this.handleItemPillClick(ev.detail.item)}
                    onSelectAll={this.handleSelectAll}
                    onTooltipContextAction={(ev) => {
                      const {name} = ev.detail;
                      this.props.onModalOpen?.();
                      if ([ActionType.ShareCohort, ActionType.ShareCustomProperty].includes(name)) {
                        this.handleShareTooltipContextAction(ev);
                      } else {
                        const result = this.handleTooltipContextAction(ev);
                        if (!result || !result.handled) {
                          propagateEvent(this.rootEl.current, ev);
                        }
                      }
                    }}
                    onTooltipContextEditDescription={(ev) => this.props.onTooltipContextEditDescription?.(ev)}
                    sections={this.getMenuSections()}
                    keydownProxyEl={!this.props.isModalOpen && this.searchInputEl.current}
                    sectionsFromInputFn={this.sectionsFromInput}
                    customSearchMatcherFn={(item, searchString) => fuzzyMatch(item.searchableContent, searchString)}
                    contextPanelTransform={this.contextPanelTransform}
                    contextPanelPositionParentElement={this.rootEl.current}
                  />
                )}
                {this.shouldShowApplyButton() && (
                  <div className='mp-properties-menu-footer'>
                    {this.getArraySelection()?.length > 0 && (
                      <div className='mp-properties-menu-footer-selection'>
                        {this.t(`query-builder.properties-menu.num-selected`, {count: this.getArraySelection().length})}
                        <ButtonReact
                          role='button'
                          elref='clearSelection'
                          theme={ButtonTheme.Tertiary}
                          size={ButtonSize.Medium}
                          onClick={() => this.clearArraySelection()}
                        >
                          {this.t(`query-builder.properties-menu.clear`)}
                        </ButtonReact>
                      </div>
                    )}
                    <ButtonReact
                      role='button'
                      elref='applyButton'
                      theme={ButtonTheme.Primary}
                      disabled={this.getApplyButtonDisabledReason() !== ApplyButtonDisabledReason.None}
                      onClick={() =>
                        this.props.onSelect?.({
                          item: this.getArraySelection(),
                          rawItem: null,
                          autoTriggered: false,
                          onItemClicked: false,
                          commit: true,
                        })
                      }
                    >
                      {this.props.applyButtonText || this.t(`query-builder.properties-menu.select`)}
                      {this.getApplyButtonDisabledReason() !== ApplyButtonDisabledReason.None ? (
                        <TooltipReact text={this.getApplyButtonDisabledMessage()} />
                      ) : null}
                    </ButtonReact>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
}

export const PropertiesMenuReact = withStyles(
  React.forwardRef<PropertiesMenu, PropertiesMenuParams>((props, ref) => {
    const eventsController = useEventsController();
    const currentProject = useCurrentProject();
    const i18n = useTranslation();

    return (
      <PropertiesMenu
        ref={ref}
        eventsController={eventsController}
        currentProject={currentProject}
        i18n={i18n}
        {...props}
      />
    );
  }),
  css,
);
